import { DashboardState, DesignerMessageType, ESdChangeReason, IRenderFrameMessage } from "@kuika/kuika-cl-model";
import {
  IBranchTreeNode,
  IConflictDto,
  IConflictRegionDto,
  IDiffItem,
  IMergeRequestDto,
  IMergeResultDto
} from "@kuika/kuika-cl-model/src/dtos/branch.dto";
import { Editor } from "@monaco-editor/react";
import { Tree, TreeProps } from "antd";
import type * as Monaco from "monaco-editor";
import React from "react";
import { connect } from "react-redux";
import BranchingService from "../../../services/branching-service";
import { IWithSDProps, withSD } from "../../../shared/hoc/screen-design-consumer";
import { sortBranchesMasterFirst } from "../../../shared/utility/branch-helpers";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import { setNextCallbackFunction } from "../../../store/unsaved-changes-modal/actions";
import KuikaButton from "../../ui-kit/kuika-button/kuika-button";
import KuikaOverflowText from "../../ui-kit/kuika-overflow-text/kuika-overflow-text";
import { OVERFLOW_REVEAL_HOST_CLASS } from "../../ui-kit/kuika-overflow-text/overflow-reveal-controller";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import KuikaTabs from "../../ui-kit/kuika-tabs/kuika-tabs";
import { toastService, type ToastId } from "../../ui-kit/kuika-toast/toast-service";
import KuikaTooltip from "../../ui-kit/kuika-tooltip/kuika-tooltip";
import { CODE_EDITOR_THEME, defineCodeEditorTheme, watchCodeEditorTheme } from "../code-editor-shell/code-editor-theme";
import "./branch-merge.scss";
import { describeScreenConflict } from "./branch-change-captions";
import { suggestRegionResolution } from "./branch-conflict-suggestions";
import { BranchMergeActions } from "./branch-merge-actions";
import { BranchMergeModals } from "./branch-merge-modals";
import type {
  ConflictTreeItem,
  IBranchMergeProps,
  IBranchMergeState,
  IPreviewPaneDiagnostics,
  PreviewPaneKey
} from "./branch-merge-types";
import { autoResolveLinkedXmlRegions, hasRedundantXml, isXmlRegion } from "./branch-merge-utils";
import { entityTypeFromFilePath } from "./entity-content-summary";
import { EntityDiffCard } from "./entity-diff-card";
import MergeSetupPanel from "./merge-setup-panel";
import { WorkflowConflictView } from "./workflow-conflict-view";

/**
 * The server said the merge has conflicts but sent none to resolve. It is a failure of the merge,
 * so it rejects the merge's promise — its own message is what the merge toast shows.
 */
class MergeContractMismatchError extends Error {}

/** What the merge toast says when the merge (or its completion) could not run. */
const describeMergeFailure = (reason: unknown, prefix: string): string => {
  if (reason instanceof MergeContractMismatchError) return reason.message;
  const message: string | undefined = reason instanceof Error ? reason.message : undefined;
  return `${prefix}: ${message || "An error occurred. Please try again."}`;
};

// One confident auto-suggestion to apply during bulk "Apply suggestions". `whole` = a redundant-xml
// file resolved as one unit; otherwise a single granular region (regionIndex).
interface SuggestionAction {
  filePath: string;
  whole: boolean;
  regionIndex: number;
  side: "target" | "source";
}

class BranchMergeComponent extends React.Component<IBranchMergeProps & IWithSDProps, IBranchMergeState> {
  private basePreviewFrameRef = React.createRef<HTMLIFrameElement>();
  private targetPreviewFrameRef = React.createRef<HTMLIFrameElement>();
  private sourcePreviewFrameRef = React.createRef<HTMLIFrameElement>();
  private previewFocusedElements: Partial<Record<PreviewPaneKey, string>> = {};

  private static readonly ignoredNoiseFields = new Set<string>([
    "CreatedAt",
    "UpdatedAt",
    "UpdatedDate",
    "CreatedDate",
    "LastModifiedAt",
    "LastSyncTime",
    "Timestamp",
    "RowVersion",
    "UpdatedAtTimestamp",
    "ExcludeFromResult"
  ]);

  private createInitialPreviewDiagnostics = (): Record<PreviewPaneKey, IPreviewPaneDiagnostics> => ({
    base: {
      snapshotReady: false,
      iframeReady: false,
      postAttempts: 0,
      ackReceived: false,
      agentStateReady: false,
      renderedItemCount: 0,
      lastEvent: "idle"
    },
    target: {
      snapshotReady: false,
      iframeReady: false,
      postAttempts: 0,
      ackReceived: false,
      agentStateReady: false,
      renderedItemCount: 0,
      lastEvent: "idle"
    },
    source: {
      snapshotReady: false,
      iframeReady: false,
      postAttempts: 0,
      ackReceived: false,
      agentStateReady: false,
      renderedItemCount: 0,
      lastEvent: "idle"
    }
  });

  constructor(props: IBranchMergeProps & IWithSDProps) {
    super(props);
    this.state = {
      currentBranch: null,
      branchToMerge: null,
      mergeData: null,
      showMergeToBranchModal: false,
      showChangesFoundModal: false,
      selectedConflicts: null,
      selectedKey: null,
      expandedKeys: [],
      mergeResult: undefined,
      allConflictsResolved: false,
      selectedChanges: null,
      pendingStagedOpIds: [],
      changesFoundCommitMessage: "",
      changesFoundInProgress: false,
      activeConflictFilePath: null,
      activeConflictRegionIndex: 0,
      manualResolvedDraft: "",
      previewLayout: "target",
      previewFullscreen: false,
      showRawValuePanels: false,
      showAdvancedActions: false,
      showAdvancedPreview: false,
      activeWorkspaceSection: "resolve",
      previewDiagnostics: this.createInitialPreviewDiagnostics(),
      isCompletingMerge: false
    };
  }

  private updatePreviewDiagnostics = (pane: PreviewPaneKey, patch: Partial<IPreviewPaneDiagnostics>) => {
    if (this.isUnmounted) return;
    this.setState((prev) => ({
      previewDiagnostics: {
        ...prev.previewDiagnostics,
        [pane]: {
          ...prev.previewDiagnostics[pane],
          ...patch
        }
      }
    }));
  };

  private resetPreviewDiagnostics = () => {
    this.setState({ previewDiagnostics: this.createInitialPreviewDiagnostics() });
  };

  private getResultFileConflicts = (result?: IMergeResultDto): IConflictDto[] => {
    if (!result) return [];

    const files: IConflictDto[] = [];
    const seen = new Set<string>();
    const walk = (nodes?: IBranchTreeNode[]) => {
      if (!nodes) return;
      for (const node of nodes) {
        (node.fileConflicts || []).forEach((conflict) => {
          if (!seen.has(conflict.filePath)) {
            seen.add(conflict.filePath);
            files.push(conflict);
          }
        });
        walk(node.children);
      }
    };

    walk(result.conflictTree);
    return files;
  };

  private extractOpIdsFromStagedEntities = (entities: any[]): string[] => {
    if (!Array.isArray(entities)) return [];
    const ids = entities.flatMap((entity) =>
      Array.isArray(entity?.patch)
        ? entity.patch
            .map((op: any) => op?.opId)
            .filter((id: string | undefined): id is string => typeof id === "string" && id.length > 0)
        : []
    );
    return Array.from(new Set(ids));
  };

  private isNoiseOnlyDiff = (diffContent?: string): boolean => {
    if (!diffContent || !diffContent.trim()) return false;

    const keyRegex = /^\s*[+-]\s*"([^"]+)"\s*:/gm;
    const changedKeys = new Set<string>();
    let match: RegExpExecArray | null;

    while ((match = keyRegex.exec(diffContent)) !== null) {
      if (match[1]) changedKeys.add(match[1]);
    }

    if (changedKeys.size === 0) return false;
    return Array.from(changedKeys).every((key) => BranchMergeComponent.ignoredNoiseFields.has(key));
  };

  private filterMeaningfulChanges = (changes: IDiffItem[]): IDiffItem[] => {
    if (!changes || changes.length === 0) return [];
    return changes.filter((change) => {
      const rawType = String(change.changeType ?? "").toLowerCase();
      const isModified = rawType === "2" || rawType === "modified" || rawType === "m";
      if (!isModified) return true;
      return !this.isNoiseOnlyDiff(change.diffContent);
    });
  };

  private isUnmounted: boolean = false;

  private codeThemeObserver: MutationObserver | undefined;

  componentWillUnmount(): void {
    // The preview-retry setTimeouts (focus/send retries, conflict refresh) can
    // keep firing for ~2-3s after the merge modal closes. Flag unmount so their
    // continuations and setState calls become no-ops.
    this.isUnmounted = true;
    this.codeThemeObserver?.disconnect();
  }

  /**
   * Monaco has one theme for the whole page, so every editor here declares the shared code theme
   * rather than a fixed `vs`, and the first one to mount installs the observer that re-declares it
   * when the appearance flips.
   */
  private handleEditorBeforeMount = (monacoInstance: typeof Monaco): void => {
    defineCodeEditorTheme(monacoInstance);
    if (!this.codeThemeObserver) {
      this.codeThemeObserver = watchCodeEditorTheme(monacoInstance);
    }
  };

  componentDidMount = async () => {
    // Review mode (merge-request approval): open directly on the supplied conflict result — skip the
    // setup panel entirely. Setting mergeResult makes render's `showSetup = !mergeResult` false.
    const review = this.props.reviewMode;
    if (review) {
      const reviewMergeData = {
        targetBranchId: review.targetBranchId,
        sourceBranchId: review.sourceBranchId,
        mergeMessage: ""
      } as IMergeRequestDto;
      const reviewFileConflicts = this.getResultFileConflicts(review.initialResult);
      const reviewAllResolved =
        reviewFileConflicts.length > 0 ? this.checkAllConflictsResolved(reviewFileConflicts) : true;
      this.setState({
        mergeData: reviewMergeData,
        currentBranch: this.props.sd.currentBranch?.name ?? null,
        branchToMerge: review.targetBranchName ?? null,
        mergeResult: review.initialResult,
        allConflictsResolved: reviewAllResolved
      });
      return;
    }

    const sourceBranchId = this.props.sd.currentBranch?.id;
    const currentBranchName = this.props.sd.currentBranch?.name ?? null;
    const md = {
      targetBranchId: this.props.sd.currentBranch?.id,
      sourceBranchId,
      mergeMessage: ""
    } as IMergeRequestDto;

    // Merge setup is now integrated inline (see render → MergeSetupPanel) instead
    // of popping a modal the moment the tab opens.
    this.setState({
      mergeData: md,
      currentBranch: currentBranchName
    });
  };

  componentDidUpdate = (
    prevProps: Readonly<IBranchMergeProps & IWithSDProps>,
    prevState: Readonly<IBranchMergeState>
  ) => {
    const conflictSelectionChanged =
      prevState.selectedConflicts !== this.state.selectedConflicts ||
      prevState.activeConflictFilePath !== this.state.activeConflictFilePath ||
      prevState.activeConflictRegionIndex !== this.state.activeConflictRegionIndex;

    if (conflictSelectionChanged) {
      const { selectedConflicts, activeConflictFilePath, activeConflictRegionIndex } = this.state;
      const activeFile =
        selectedConflicts?.find((f) => f.filePath === activeConflictFilePath) || selectedConflicts?.[0];
      const activeRegion = activeFile?.conflicts?.[activeConflictRegionIndex] || activeFile?.conflicts?.[0];

      this.resetPreviewDiagnostics();
      this.schedulePreviewRefresh();
    }
  };

  private schedulePreviewRefresh = () => {
    setTimeout(() => this.refreshActiveConflictPreview(), 0);
    setTimeout(() => this.refreshActiveConflictPreview(), 500);
  };

  private normalizeDesignSnapshot = (raw: any): any | null => {
    if (!raw) return null;

    try {
      const tryParse = (value: any): any | null => {
        if (!value) return null;
        if (Array.isArray(value?.displayItems)) return value;

        if (typeof value === "string") {
          try {
            const parsed = JSON.parse(value);
            return tryParse(parsed) ?? parsed;
          } catch {
            return null;
          }
        }

        if (typeof value === "object") {
          if (typeof value.displayItems === "string") {
            try {
              const parsedDisplayItems = JSON.parse(value.displayItems);
              if (Array.isArray(parsedDisplayItems)) {
                return { ...value, displayItems: parsedDisplayItems };
              }
              if (Array.isArray(parsedDisplayItems?.displayItems)) {
                return { ...value, displayItems: parsedDisplayItems.displayItems };
              }
            } catch {}
          }

          const wrapped =
            value.JSON ??
            value.Json ??
            value.json ??
            value.JsonEntity ??
            value.jsonEntity ??
            value.screenDesign ??
            value.ScreenDesign ??
            value.document ??
            value.Document ??
            value.value ??
            value.Value ??
            value.data ??
            value.Data;
          if (wrapped != null) {
            const unwrapped = tryParse(wrapped);
            if (unwrapped) return unwrapped;
          }

          if (Array.isArray(value.displayItems)) return value;
        }

        return null;
      };

      const direct = tryParse(raw);
      if (direct) return direct;

      if (typeof raw === "string") {
        try {
          const parsed = JSON.parse(raw);
          const normalized = tryParse(parsed);
          if (normalized) return normalized;
        } catch {}
      }

      const queue: any[] = [raw];
      const visited = new Set<any>();

      while (queue.length > 0) {
        const current = queue.shift();
        if (!current || typeof current !== "object" || visited.has(current)) continue;
        visited.add(current);

        const normalized = tryParse(current);
        if (normalized) return normalized;

        for (const key of Object.keys(current)) {
          const child = current[key];
          if (child && typeof child === "object") {
            queue.push(child);
          } else if (typeof child === "string") {
            try {
              queue.push(JSON.parse(child));
            } catch {}
          }
        }
      }

      return null;
    } catch (error) {
      console.warn("Failed to normalize design snapshot:", error, "raw value:", raw);
      return null;
    }
  };

  private isPreviewableDesignConflict = (filePath?: string): boolean => {
    if (!filePath) return false;
    const normalized = filePath.toLowerCase();
    return normalized.includes("screendesign") || normalized.includes("screen") || normalized.endsWith(".json");
  };

  /**
   * True only for screen-design conflicts. The canvas preview renders a screen tree, so it is
   * meaningless for actions, workflows, datasources, etc. Non-screen conflicts carry their
   * entity-type enum name in the path (KWorkFlow, KAction, ...); screen conflicts live under the
   * "ScreenDesigns" folder (which `entityTypeFromFilePath` does not map), so fall back to that.
   */
  private isScreenDesignConflict = (filePath?: string): boolean => {
    if (!filePath) return false;
    const detectedType = entityTypeFromFilePath(filePath);
    if (detectedType !== null) return detectedType === 3; // 3 = KScreenDesign
    return filePath.toLowerCase().includes("screendesign");
  };

  private resolvePathSegment = (cursor: any, segment: string): any => {
    if (cursor == null) return undefined;

    const idMatch = segment.match(/^\[id=(.+)\]$/);
    if (idMatch) {
      const idValue = idMatch[1];
      if (!Array.isArray(cursor)) return undefined;
      return cursor.find(
        (item: any) =>
          item != null &&
          (String(item.id) === idValue ||
            String(item.kuikaId) === idValue ||
            String(item.displayItemId) === idValue ||
            String(item.displayItemID) === idValue)
      );
    }

    if (Array.isArray(cursor) && /^\d+$/.test(segment)) {
      return cursor[parseInt(segment, 10)];
    }

    return cursor[segment];
  };

  private getSnapshotValueAtConflictPath = (snapshot: any, conflictPath?: string): any => {
    const design = this.normalizeDesignSnapshot(snapshot);
    if (!design) return undefined;

    const normalizedPath = String(conflictPath || "")
      .replace(/^\/JSON\//, "")
      .replace(/^\//, "");

    if (!normalizedPath) return undefined;

    const segments = normalizedPath.split("/").filter(Boolean);
    let cursor: any = design;

    for (const segment of segments) {
      cursor = this.resolvePathSegment(cursor, segment);
      if (cursor == null) return undefined;
    }

    return cursor;
  };

  private formatConflictFocusValue = (value: any): string => {
    if (value == null) return "—";
    if (typeof value === "string") return value;
    if (typeof value === "number" || typeof value === "boolean") return String(value);
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  };

  private shouldRenderPreviewSection = (filePath?: string, _region?: IConflictRegionDto): boolean => {
    // Canvas preview is a screen-design-only surface. Non-screen entities now also carry snapshots,
    // so gate strictly on screen designs rather than "has snapshots" (which would show a useless
    // empty canvas for actions/workflows).
    return this.isScreenDesignConflict(filePath);
  };

  private getConflictHighlightId = (snapshot: any, conflictPath?: string): string | null => {
    const design = this.normalizeDesignSnapshot(snapshot);
    if (!design) return null;

    const normalizedPath = String(conflictPath || "")
      .replace(/^\/JSON\//, "")
      .replace(/^\//, "");

    if (!normalizedPath) return null;

    const segments = normalizedPath.split("/").filter(Boolean);
    let cursor: any = design;
    let lastCandidate: any = null;

    const pickId = (obj: any) => obj?.displayItemId ?? obj?.displayItemID ?? obj?.id ?? obj?.kuikaId;

    for (const segment of segments) {
      cursor = this.resolvePathSegment(cursor, segment);
      if (cursor && typeof cursor === "object") {
        const candidate = pickId(cursor);
        if (candidate != null) {
          lastCandidate = candidate;
        }
      }
      if (cursor == null) break;
    }

    if (lastCandidate == null) return null;
    if (typeof lastCandidate === "number") return String(lastCandidate);
    const asString = String(lastCandidate);
    if (/^\d+$/.test(asString)) return String(parseInt(asString, 10));
    return asString;
  };

  private applyPersistentPreviewFocus = (
    pane: PreviewPaneKey,
    frameRef: React.RefObject<HTMLIFrameElement>,
    kuikaId?: string | null
  ) => {
    if (!kuikaId) return;

    const focusWithRetry = (attempt = 0) => {
      if (this.isUnmounted) return;
      const doc = frameRef.current?.contentWindow?.document;
      if (!doc) {
        if (attempt < 8) setTimeout(() => focusWithRetry(attempt + 1), 220);
        return;
      }

      let styleTag = doc.getElementById("branch-merge-preview-focus-style") as HTMLStyleElement | null;
      if (!styleTag) {
        styleTag = doc.createElement("style");
        styleTag.id = "branch-merge-preview-focus-style";
        // The preview iframe loads the agent bundle, not the designer's stylesheet, so its
        // document has no --blue-5. Resolve the brand accent from the host and inline it.
        const accent: string = getComputedStyle(document.documentElement).getPropertyValue("--blue-5").trim();
        styleTag.textContent = `
          .branch-merge-preview-focus {
            outline: 2px solid ${accent} !important;
            outline-offset: 2px !important;
            box-shadow: 0 0 0 2px color-mix(in srgb, ${accent} 22%, transparent) !important;
          }
        `;
        doc.head?.appendChild(styleTag);
      }

      const selectors = [
        `#kuika_${CSS.escape(String(kuikaId))}`,
        `#${CSS.escape(String(kuikaId))}`,
        `[kuikaid="${String(kuikaId).replace(/"/g, '\\"')}"]`,
        `[data-kuika-id="${String(kuikaId).replace(/"/g, '\\"')}"]`
      ];

      let target: Element | null = null;
      for (const selector of selectors) {
        target = doc.querySelector(selector);
        if (target) break;
      }

      if (!target) {
        if (attempt < 12) setTimeout(() => focusWithRetry(attempt + 1), 240);
        return;
      }

      const previousId = this.previewFocusedElements[pane];
      if (previousId && previousId !== String(kuikaId)) {
        const previous = doc.querySelector(`#kuika_${CSS.escape(previousId)}`) || doc.getElementById(previousId);
        previous?.classList?.remove("branch-merge-preview-focus");
      }

      target.classList.add("branch-merge-preview-focus");
      this.previewFocusedElements[pane] = String(kuikaId);

      // Manual scroll instead of scrollIntoView — scrollIntoView propagates across
      // same-origin iframe boundaries and scrolls the parent kuika_layout by 48px.
      try {
        const scrollParent = doc.getElementById("kuika_scrollFrame") || doc.documentElement;
        const targetRect = (target as HTMLElement).getBoundingClientRect();
        const parentRect = scrollParent.getBoundingClientRect();
        scrollParent.scrollTop += targetRect.top - parentRect.top - parentRect.height / 2 + targetRect.height / 2;
      } catch {}
    };

    focusWithRetry();
  };

  private postSnapshotToPreview = (
    pane: PreviewPaneKey,
    frameRef: React.RefObject<HTMLIFrameElement>,
    screenDesign: any
  ) => {
    const frame = frameRef.current;
    if (!frame) {
      this.updatePreviewDiagnostics(pane, { iframeReady: false, lastEvent: "frame-missing" });
      return;
    }

    const normalizedDesign = this.normalizeDesignSnapshot(screenDesign);
    if (!normalizedDesign) {
      this.updatePreviewDiagnostics(pane, { snapshotReady: false, lastEvent: "snapshot-invalid" });
      return;
    }

    this.updatePreviewDiagnostics(pane, {
      snapshotReady: true,
      iframeReady: !!frame.contentWindow,
      ackReceived: false,
      agentStateReady: false,
      renderedItemCount: 0,
      postAttempts: 0,
      lastEvent: frame.contentWindow ? "ready-to-post" : "waiting-iframe"
    });

    const sendWithRetry = (attempt = 0) => {
      if (this.isUnmounted) return;
      const win = frame.contentWindow;
      if (!win) {
        this.updatePreviewDiagnostics(pane, { iframeReady: false, lastEvent: "waiting-iframe" });
        if (attempt < 6) {
          setTimeout(() => sendWithRetry(attempt + 1), 200);
        }
        return;
      }

      this.updatePreviewDiagnostics(pane, { iframeReady: true, postAttempts: attempt + 1, lastEvent: "posting" });

      const liveCanvas = document.getElementById("kuika_iframeAgent_canvas") as HTMLIFrameElement | null;
      const project =
        (liveCanvas?.contentWindow as any)?.kuika?.activeKuikaReactInstance?.state?.project ??
        (window as any)?.kuika?.activeKuikaReactInstance?.state?.project ??
        this.props.sd.project;

      const projectTempStyles = project?.projectTempStyles ?? this.props.sd.projectTempStyles ?? {};

      const payload = {
        designerMessage: {
          messageType: DesignerMessageType.renderFrameMessage,
          messageId: Math.random().toString(),
          message: {
            screenDesign: normalizedDesign,
            project: project ?? undefined,
            projectTempStyles,
            dashboardState: DashboardState.design,
            changeReason: ESdChangeReason.OtherSetStates,
            previewOnly: true
          } as IRenderFrameMessage
        }
      };

      let ack = false;
      const onMessage = (ev: MessageEvent) => {
        if (ev.source === win) {
          ack = true;
          this.updatePreviewDiagnostics(pane, { ackReceived: true, lastEvent: "ack-received" });
          window.removeEventListener("message", onMessage);
        }
      };

      window.addEventListener("message", onMessage);

      try {
        win.postMessage(payload, "*");
      } catch {
        this.updatePreviewDiagnostics(pane, { lastEvent: "post-failed" });
        window.removeEventListener("message", onMessage);
        return;
      }

      setTimeout(() => {
        try {
          const agentState = (win as any)?.kuika?.activeKuikaReactInstance?.state;
          const design = agentState?.screenDesign;
          const count = Array.isArray(design?.displayItems) ? design.displayItems.length : 0;
          const ready = !!design && count >= 0;
          this.updatePreviewDiagnostics(pane, {
            agentStateReady: ready,
            renderedItemCount: count,
            lastEvent: ready ? "agent-state-updated" : "agent-state-empty"
          });
        } catch {
          this.updatePreviewDiagnostics(pane, { lastEvent: "agent-state-unreadable" });
        }
      }, 220);

      setTimeout(() => {
        window.removeEventListener("message", onMessage);
        if (!ack && attempt < 10) {
          this.updatePreviewDiagnostics(pane, { lastEvent: "retrying" });
          sendWithRetry(attempt + 1);
        } else if (!ack) {
          this.updatePreviewDiagnostics(pane, { lastEvent: "no-ack-timeout" });
        }
      }, 500);
    };

    if (!frame.contentWindow) {
      const onLoad = () => {
        try {
          frame.removeEventListener("load", onLoad);
        } catch {}
        this.updatePreviewDiagnostics(pane, { iframeReady: true, lastEvent: "iframe-loaded" });
        sendWithRetry();
      };
      frame.addEventListener("load", onLoad);
      setTimeout(() => sendWithRetry(), 350);
      return;
    }

    sendWithRetry();
  };

  private refreshActiveConflictPreview = () => {
    if (this.isUnmounted) return;
    const { selectedConflicts, activeConflictFilePath, activeConflictRegionIndex } = this.state;
    if (!selectedConflicts || selectedConflicts.length === 0) return;

    const activeFile = selectedConflicts.find((f) => f.filePath === activeConflictFilePath) || selectedConflicts[0];
    if (!activeFile) return;

    const activeRegion = activeFile.conflicts?.[activeConflictRegionIndex] || activeFile.conflicts?.[0];
    if (!activeRegion) return;

    if (!this.shouldRenderPreviewSection(activeFile.filePath, activeRegion)) return;

    const baseSnapshot = this.normalizeDesignSnapshot((activeRegion as any).baseSnapshot || (activeRegion as any).base);
    const targetSnapshot = this.normalizeDesignSnapshot((activeRegion as any).oursSnapshot || activeRegion.ours);
    const sourceSnapshot = this.normalizeDesignSnapshot((activeRegion as any).theirsSnapshot || activeRegion.theirs);
    const conflictPath = (activeRegion as any)?.conflictPath;

    const baseHighlightId = this.getConflictHighlightId(
      (activeRegion as any).baseSnapshot || (activeRegion as any).base,
      conflictPath
    );
    const targetHighlightId = this.getConflictHighlightId(
      (activeRegion as any).oursSnapshot || activeRegion.ours,
      conflictPath
    );
    const sourceHighlightId = this.getConflictHighlightId(
      (activeRegion as any).theirsSnapshot || activeRegion.theirs,
      conflictPath
    );

    this.updatePreviewDiagnostics("base", {
      snapshotReady: !!baseSnapshot,
      iframeReady: !!this.basePreviewFrameRef.current?.contentWindow,
      ackReceived: false,
      agentStateReady: false,
      renderedItemCount: 0,
      postAttempts: 0,
      lastEvent: baseSnapshot ? "snapshot-ready" : "snapshot-missing"
    });
    this.updatePreviewDiagnostics("target", {
      snapshotReady: !!targetSnapshot,
      iframeReady: !!this.targetPreviewFrameRef.current?.contentWindow,
      ackReceived: false,
      agentStateReady: false,
      renderedItemCount: 0,
      postAttempts: 0,
      lastEvent: targetSnapshot ? "snapshot-ready" : "snapshot-missing"
    });
    this.updatePreviewDiagnostics("source", {
      snapshotReady: !!sourceSnapshot,
      iframeReady: !!this.sourcePreviewFrameRef.current?.contentWindow,
      ackReceived: false,
      agentStateReady: false,
      renderedItemCount: 0,
      postAttempts: 0,
      lastEvent: sourceSnapshot ? "snapshot-ready" : "snapshot-missing"
    });

    if (baseSnapshot) {
      this.postSnapshotToPreview("base", this.basePreviewFrameRef, baseSnapshot);
      this.applyPersistentPreviewFocus("base", this.basePreviewFrameRef, baseHighlightId);
    }

    if (targetSnapshot) {
      this.postSnapshotToPreview("target", this.targetPreviewFrameRef, targetSnapshot);
      this.applyPersistentPreviewFocus("target", this.targetPreviewFrameRef, targetHighlightId);
    }
    if (sourceSnapshot) {
      this.postSnapshotToPreview("source", this.sourcePreviewFrameRef, sourceSnapshot);
      this.applyPersistentPreviewFocus("source", this.sourcePreviewFrameRef, sourceHighlightId);
    }
  };

  private renderPreviewDiagnostics = (pane: PreviewPaneKey) => {
    const diag = this.state.previewDiagnostics[pane];
    const ackLabel = diag.ackReceived ? "yes" : "no";
    return (
      <div className="branch-merge__preview-diagnostics" title={`Last: ${diag.lastEvent}`}>
        snapshot:{diag.snapshotReady ? "yes" : "no"} • iframe:{diag.iframeReady ? "yes" : "no"} • posts:
        {diag.postAttempts} • ack:{ackLabel} • agent:{diag.agentStateReady ? "yes" : "no"} • items:
        {diag.renderedItemCount}
      </div>
    );
  };

  private jumpToNextUnresolvedRegion = () => {
    const { selectedConflicts, activeConflictFilePath, activeConflictRegionIndex } = this.state;
    if (!selectedConflicts || selectedConflicts.length === 0) return;

    const orderedEntries: Array<{ filePath: string; regionIndex: number; resolved: boolean }> = [];
    selectedConflicts.forEach((file) => {
      file.conflicts.forEach((region, regionIndex) => {
        // Skip Xml regions that are auto-managed via Json
        if (hasRedundantXml(file.filePath) && isXmlRegion(region)) return;
        orderedEntries.push({ filePath: file.filePath, regionIndex, resolved: !!region.isResolved });
      });
    });

    if (orderedEntries.length === 0) return;

    const currentIndex = orderedEntries.findIndex(
      (entry) => entry.filePath === activeConflictFilePath && entry.regionIndex === activeConflictRegionIndex
    );

    for (let i = 1; i <= orderedEntries.length; i++) {
      const next = orderedEntries[(Math.max(currentIndex, -1) + i) % orderedEntries.length];
      if (!next.resolved) {
        this.setState(
          {
            activeConflictFilePath: next.filePath,
            activeConflictRegionIndex: next.regionIndex
          },
          this.refreshActiveConflictPreview
        );
        return;
      }
    }

    KMainFunctions.displayInfoNotification("All conflict regions are already resolved.");
  };

  /** What the merge toast says once the merge has answered. */
  private describeMergeOutcome = (result: IMergeResultDto, targetBranchName: string | null): string => {
    if (result.isEmpty) {
      return `No changes to merge. ${this.props.sd.currentBranch?.name} is already up to date with ${targetBranchName}.`;
    }
    if (result.conflictTree && result.conflictTree.length > 0) {
      return this.getResultFileConflicts(result).length > 0
        ? "Conflicts found — resolve them to finish the merge."
        : "Changes merged — review them to finish the merge.";
    }
    if (result.mergeRequestCreated) {
      return "Merge request submitted for approval. The branch owner will review it.";
    }
    return "No changes detected in merge.";
  };

  /**
   * A merge is a long job, so one toast carries it from "Merging" to its outcome — up to date,
   * conflicts to resolve, a request submitted, or a failure — where each outcome used to raise a
   * toast of its own once the answer came.
   */
  private executeMergeRequest = (mergeData: IMergeRequestDto, targetBranchName: string | null) => {
    let toastId: ToastId | null = null;
    const work: Promise<IMergeResultDto> = (async (): Promise<IMergeResultDto> => {
      let result: IMergeResultDto;
      try {
        result = await this.props.sd.mergeBranch(mergeData);
      } catch (error: unknown) {
        // The service has already reported the failure in its own toast; this one steps aside
        // before the rejection reaches it, rather than say the same thing twice.
        if (toastId !== null) toastService.dismiss(toastId);
        throw error;
      }
      // Thrown here rather than by the service, so this one still belongs to the promise toast.
      const hasConflictContractMismatch: boolean =
        !!result.hasConflicts && !result.isEmpty && this.getResultFileConflicts(result).length === 0;
      if (hasConflictContractMismatch) {
        console.error(
          "Merge conflict contract mismatch: hasConflicts=true but conflictTree has no file conflicts.",
          result
        );
        throw new MergeContractMismatchError(
          "Merge returned inconsistent conflict data. Please retry merge or contact support."
        );
      }
      return result;
    })();
    toastId = toastService.promise(work, {
      loading: `Merging into ${targetBranchName ?? "the target branch"}…`,
      success: (result: IMergeResultDto): string => this.describeMergeOutcome(result, targetBranchName),
      error: (reason: unknown): string => describeMergeFailure(reason, "Merge failed")
    });

    work
      .then((result) => {
        if (this.isUnmounted) return;
        if (result.isEmpty) {
          this.props.setLoading(false);
          this.props.endMerge();
          return;
        }

        const hasChanges = result.conflictTree && result.conflictTree.length > 0;
        const resultFileConflicts = this.getResultFileConflicts(result);
        const hasConflicts = resultFileConflicts.length > 0;

        if (hasChanges) {
          const allConflictsResolved = hasConflicts ? this.checkAllConflictsResolved(resultFileConflicts) : true;

          this.setState({
            currentBranch: this.props.sd.currentBranch?.name ?? null,
            branchToMerge: targetBranchName,
            showMergeToBranchModal: false,
            mergeResult: result,
            allConflictsResolved
          });
          this.props.setLoading(false);
        } else if (result.mergeRequestCreated) {
          // Clean merge into a protected branch by a non-owner → captured as a request at detection.
          this.props.setLoading(false);
          this.props.endMerge();
        } else {
          this.props.setLoading(false);
          this.props.endMerge();
        }
      })
      .catch((error) => {
        if (this.isUnmounted) return;
        console.error("Error during merge:", error);
        this.props.setLoading(false);
        // Inconsistent conflict data leaves nothing to resolve: the merge ends, as it did before.
        if (error instanceof MergeContractMismatchError) {
          this.props.endMerge();
        }
      });
  };

  handleSelect: TreeProps["onSelect"] = (selectedKeys) => {
    const selectedKey = selectedKeys[0]?.toString();
    if (!selectedKey) {
      this.setState({ selectedConflicts: null, selectedChanges: null, selectedKey });
      return;
    }

    // Find all conflicts from the selected node and its children
    const findConflictsInTree = (tree: IBranchTreeNode[] | undefined, key: string): IConflictDto[] => {
      if (!tree) return [];

      const conflicts: IConflictDto[] = [];
      const seenFilePaths = new Set<string>();

      const selectedNode = tree.find((node) => {
        if (node.id === key) return true;
        if (node.fileConflicts?.some((fc) => fc.filePath === key)) return true;
        return false;
      });

      if (selectedNode) {
        // Found the node, now collect all conflicts from it and its descendants
        const collectAllConflicts = (node: IBranchTreeNode): void => {
          // Add this node's conflicts if it has any
          if (node.fileConflicts && node.fileConflicts.length > 0) {
            if (!seenFilePaths.has(node.fileConflicts[0].filePath)) {
              seenFilePaths.add(node.fileConflicts[0].filePath);
              conflicts.push(...node.fileConflicts);
            }
          }

          // Recursively collect from all children
          if (node.children && node.children.length > 0) {
            node.children.forEach(collectAllConflicts);
          }
        };

        collectAllConflicts(selectedNode);
        return conflicts;
      }

      // If not found at this level, search children
      for (const node of tree) {
        if (node.children && node.children.length > 0) {
          const childConflicts = findConflictsInTree(node.children, key);
          childConflicts.forEach((conflict) => {
            if (!seenFilePaths.has(conflict.filePath)) {
              seenFilePaths.add(conflict.filePath);
              conflicts.push(conflict);
            }
          });
        }
      }

      return conflicts;
    };

    const selectedConflicts = findConflictsInTree(this.state.mergeResult?.conflictTree, selectedKey);

    if (selectedConflicts && selectedConflicts.length > 0) {
      const firstRegion = selectedConflicts[0]?.conflicts?.[0];
      this.setState({
        selectedConflicts,
        selectedChanges: null,
        selectedKey,
        activeConflictFilePath: selectedConflicts[0]?.filePath || null,
        activeConflictRegionIndex: 0,
        manualResolvedDraft: this.getInitialManualResolvedDraft(firstRegion),
        showAdvancedActions: false,
        showAdvancedPreview: false,
        showRawValuePanels: false,
        previewLayout: "target"
      });
    } else {
      this.setState({ selectedConflicts: null, selectedChanges: null, selectedKey: null });
    }
  };

  handleExpand: TreeProps["onExpand"] = (expandedKeys) => {
    this.setState({ expandedKeys });
  };

  private checkAllConflictsResolved = (files: IConflictDto[] | undefined): boolean => {
    if (!files) return true;

    for (const file of files) {
      // Check if file conflicts are resolved
      if (!file.isResolved || file.conflicts?.some((region) => !region.isResolved)) {
        return false;
      }
    }

    return true;
  };

  private getConflictResolutionStats = (files: IConflictDto[] | undefined): { resolved: number; total: number } => {
    if (!files || files.length === 0) return { resolved: 0, total: 0 };

    const total = files.length;
    const resolved = files.filter((file) => file.isResolved).length;

    return { resolved, total };
  };

  private validateResolvedConflictPayload = (
    files: IConflictDto[] | undefined
  ): { isValid: boolean; message?: string } => {
    if (!files || files.length === 0) return { isValid: true };

    for (const file of files) {
      for (const region of file.conflicts || []) {
        if (!region?.isResolved) {
          return { isValid: false, message: "All conflict regions must be resolved before merge completion." };
        }

        const allowsEmptyAsDelete =
          !!(region as any)?.oursMissing || !!(region as any)?.theirsMissing || !!(region as any)?.baseMissing;
        const resolvedContent = region.resolvedContent;

        if ((resolvedContent === null || resolvedContent === undefined) && !allowsEmptyAsDelete) {
          return {
            isValid: false,
            message: "One or more resolved regions have no output. Please re-apply the resolution."
          };
        }

        if (!allowsEmptyAsDelete && typeof resolvedContent === "string" && resolvedContent.trim().length === 0) {
          return {
            isValid: false,
            message:
              "One or more resolved regions are empty in a non-delete context. Please provide a meaningful grouped resolution."
          };
        }
      }
    }

    return { isValid: true };
  };

  private getConflictTypeGuidance = (rawType?: string) => {
    const conflictType = (rawType || "edit-vs-edit").toLowerCase();

    switch (conflictType) {
      case "delete-vs-edit":
        return {
          label: "delete-vs-edit",
          hint: "Source deletes while target changes the same node. Review manually or keep base if both are invalid.",
          targetAction: "Use Target (Keep changes)",
          sourceAction: "Use Source (Apply delete)"
        };
      case "edit-vs-delete":
        return {
          label: "edit-vs-delete",
          hint: "Target deletes while source changes the same node. Review manually or keep base if both are invalid.",
          targetAction: "Use Target (Apply delete)",
          sourceAction: "Use Source (Keep changes)"
        };
      case "move-vs-edit":
        return {
          label: "move-vs-edit",
          hint: "One side reorders while the other updates content. Manual resolution is usually safest.",
          targetAction: "Use Target",
          sourceAction: "Use Source"
        };
      case "move-vs-delete":
      case "delete-vs-move":
        return {
          label: conflictType,
          hint: "One side moves and the other deletes. Confirm intended behavior before choosing one side.",
          targetAction: "Use Target",
          sourceAction: "Use Source"
        };
      case "add-vs-add":
        return {
          label: "add-vs-add",
          hint: "Both branches add the same logical item differently. Manual merge is recommended.",
          targetAction: "Use Target",
          sourceAction: "Use Source"
        };
      case "delete-vs-delete":
        return {
          label: "delete-vs-delete",
          hint: "Both branches delete this region. Keep Base only if you want to restore it manually.",
          targetAction: "Use Target (Keep deleted)",
          sourceAction: "Use Source (Keep deleted)"
        };
      case "parse-or-base-uncertain":
        return {
          label: "parse-or-base-uncertain",
          hint: "Base or snapshot is uncertain, so this conflict is forced. Prefer manual or keep base.",
          targetAction: "Use Target",
          sourceAction: "Use Source"
        };
      default:
        return {
          label: conflictType,
          hint: "Both branches changed overlapping content. Compare Base/Target/Source values before resolving.",
          targetAction: "Use Target",
          sourceAction: "Use Source"
        };
    }
  };

  private getRegionSideValue = (region: IConflictRegionDto | undefined, side: "base" | "ours" | "theirs"): string => {
    if (!region) return "";

    if (side === "base") {
      return region.baseMissing ? "" : (region as any)?.base || "";
    }

    if (side === "ours") {
      return region.oursMissing ? "" : region.ours || "";
    }

    return region.theirsMissing ? "" : region.theirs || "";
  };

  private getRegionSideCaption = (region: IConflictRegionDto | undefined, side: "base" | "ours" | "theirs"): string => {
    if (!region) return "";

    const isMissing =
      side === "base" ? !!region.baseMissing : side === "ours" ? !!region.oursMissing : !!region.theirsMissing;

    if (isMissing) {
      if (side === "ours") return "Deleted on Target";
      if (side === "theirs") return "Deleted on Source";
      return "Not present in Base";
    }

    return "Present";
  };

  private getInitialManualResolvedDraft = (region?: IConflictRegionDto): string => {
    if (!region) return "";

    const resolved = (region.resolvedContent ?? "").trim();
    if (resolved.length > 0) return region.resolvedContent || "";

    const ours = this.getRegionSideValue(region, "ours");
    if (ours.trim().length > 0) return ours;

    const theirs = this.getRegionSideValue(region, "theirs");
    if (theirs.trim().length > 0) return theirs;

    return this.getRegionSideValue(region, "base");
  };

  private getRegionResolutionLabel = (
    region?: IConflictRegionDto
  ): "pending" | "target" | "source" | "base" | "manual" => {
    if (!region || !region.isResolved) return "pending";

    const resolved = (region.resolvedContent ?? "").trim();
    const ours = (region.ours ?? "").trim();
    const theirs = (region.theirs ?? "").trim();
    const base = (((region as any)?.base as string) ?? "").trim();

    if (resolved === ours) return "target";
    if (resolved === theirs) return "source";
    if (resolved === base) return "base";
    return "manual";
  };

  private setPreviewLayout = (previewLayout: "target" | "source" | "base" | "split") => {
    this.setState({ previewLayout }, this.refreshActiveConflictPreview);
  };

  private togglePreviewFullscreen = () => {
    this.setState((prev) => ({ previewFullscreen: !prev.previewFullscreen }), this.refreshActiveConflictPreview);
  };

  private getRegionLabel = (region: IConflictRegionDto, index: number): string => {
    const status = region.isResolved ? "Resolved" : "Pending";
    return `Region ${index + 1} • ${status}`;
  };

  private humanizePathToken = (value: string): string => {
    return value
      .replace(/~1/g, "/")
      .replace(/~0/g, "~")
      .replace(/[_-]+/g, " ")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .trim();
  };

  private getDesignNodeLabel = (node: any): string | null => {
    if (!node || typeof node !== "object") return null;

    const candidates = [
      node.name,
      node.Name,
      node.friendlyName,
      node.FriendlyName,
      node.title,
      node.Title,
      node.description,
      node.Description,
      node.componentName,
      node.ComponentName,
      node.type,
      node.Type
    ];

    for (const candidate of candidates) {
      if (typeof candidate === "string" && candidate.trim()) {
        return candidate.trim();
      }
    }

    return null;
  };

  private getNodeIdValue = (node: any): string | null => {
    if (!node || typeof node !== "object") return null;
    const raw = node.id ?? node.Id ?? node.kuikaId ?? node.KuikaId;
    if (raw == null) return null;
    return String(raw);
  };

  private findNodeById = (container: any, idValue: string): any | null => {
    if (container == null) return null;

    const tryFindInArray = (arr: any[]): any | null => {
      return arr.find((item) => this.getNodeIdValue(item) === idValue) ?? null;
    };

    if (Array.isArray(container)) {
      return tryFindInArray(container);
    }

    if (typeof container === "object") {
      const listFields = ["displayItems", "children", "items", "elements"];
      for (const field of listFields) {
        const value = (container as any)[field];
        if (Array.isArray(value)) {
          const found = tryFindInArray(value);
          if (found) return found;
        }
      }
    }

    return null;
  };

  private buildReadableConflictPath = (snapshotRaw: any, rawPath: string): string | null => {
    const snapshot = this.normalizeDesignSnapshot(snapshotRaw);
    if (!snapshot || !rawPath) return null;

    const tokens = rawPath
      .split("/")
      .map((token) => token.trim())
      .filter(Boolean);

    if (tokens.length === 0) return null;

    const labels: string[] = [];
    let cursor: any = snapshot;

    for (const tokenRaw of tokens) {
      const token = tokenRaw.replace(/~1/g, "/").replace(/~0/g, "~");
      const lowerToken = token.toLowerCase();
      const idMatch = token.match(/^\[id=(.+)\]$/i);

      if (idMatch) {
        const idValue = idMatch[1]?.replace(/^"|"$/g, "").trim();
        if (!idValue) continue;

        const foundNode = this.findNodeById(cursor, idValue);
        if (foundNode) {
          const nodeLabel = this.getDesignNodeLabel(foundNode);
          labels.push(nodeLabel || `id=${idValue}`);
          cursor = foundNode;
          continue;
        }

        if (idValue !== "-1") {
          labels.push(`id=${idValue}`);
        }
        continue;
      }

      if (lowerToken === "json") {
        labels.push("JSON");
        continue;
      }

      if (lowerToken === "children") {
        if (cursor && typeof cursor === "object" && Array.isArray((cursor as any).children)) {
          cursor = (cursor as any).children;
        }
        continue;
      }

      if (cursor && typeof cursor === "object" && (cursor as any)[token] !== undefined) {
        cursor = (cursor as any)[token];
      }

      labels.push(token === "displayItems" ? "displayItems" : this.humanizePathToken(token));
    }

    if (labels.length === 0) return null;

    const cleaned = labels.filter((item, idx) => item && (idx === 0 || item !== labels[idx - 1]));
    return cleaned.join("/");
  };

  private getFriendlyConflictPathLabel = (region: IConflictRegionDto, index: number): string => {
    const rawPath = ((region as any)?.conflictPath as string) || "";

    // Prefer a plain-language caption ("Button \"SaveButton\" — text") over a JSON-pointer breadcrumb.
    const snapshotForCaption =
      this.normalizeDesignSnapshot((region as any)?.oursSnapshot) ||
      this.normalizeDesignSnapshot((region as any)?.theirsSnapshot) ||
      this.normalizeDesignSnapshot((region as any)?.baseSnapshot);
    const conflictCaption = describeScreenConflict(snapshotForCaption, rawPath);
    if (conflictCaption.componentLabel) return conflictCaption.label;

    const fromSnapshots =
      this.buildReadableConflictPath((region as any)?.oursSnapshot, rawPath) ||
      this.buildReadableConflictPath((region as any)?.theirsSnapshot, rawPath) ||
      this.buildReadableConflictPath((region as any)?.baseSnapshot, rawPath);

    if (fromSnapshots) return fromSnapshots;

    const fallback = rawPath
      .split("/")
      .map((token) => token.trim())
      .filter(Boolean)
      .map((token) => this.humanizePathToken(token.replace(/^\[id=/i, "").replace(/\]$/, "")))
      .filter((token) => !!token && token.toLowerCase() !== "children")
      .join("/");

    if (fallback) return fallback;
    return `Item ${index + 1}`;
  };

  private tryExtractDesignNameFromSnapshot = (raw: any): string | null => {
    const normalized = this.normalizeDesignSnapshot(raw);
    if (normalized && typeof normalized === "object") {
      const candidate = (normalized as any).Name ?? (normalized as any).name ?? (normalized as any).DisplayName;
      if (typeof candidate === "string" && candidate.trim()) {
        return candidate.trim();
      }
    }

    return null;
  };

  private resolveTreeDisplayName = (fallbackTitle: string, fileConflicts?: IConflictDto[]): string => {
    const conflict = fileConflicts?.[0];
    const region = conflict?.conflicts?.[0];
    const nameFromSnapshot =
      this.tryExtractDesignNameFromSnapshot((region as any)?.oursSnapshot) ||
      this.tryExtractDesignNameFromSnapshot((region as any)?.theirsSnapshot) ||
      this.tryExtractDesignNameFromSnapshot((region as any)?.baseSnapshot);

    if (nameFromSnapshot) return nameFromSnapshot;

    const path = conflict?.filePath || fallbackTitle;
    const fileName = path?.split("/").pop() || fallbackTitle;
    return fileName.replace(/\.json$/i, "");
  };

  // Maps the developer-facing folder segment (entity-type enum name or storage folder) to a friendly
  // group title in the conflict tree — "Actions", "Workflows", "Screens" instead of "KAction" etc.
  private static readonly FRIENDLY_FOLDER_NAMES: Record<string, string> = {
    kaction: "Actions",
    actions: "Actions",
    kdatasource: "Datasources",
    datasources: "Datasources",
    kworkflow: "Workflows",
    workflows: "Workflows",
    kapimanagement: "API Management",
    apimanagements: "API Management",
    knotificationtemplate: "Notifications",
    notifications: "Notifications",
    kscheduledjobs: "Scheduled Jobs",
    scheduledjobs: "Scheduled Jobs",
    kofflinetable: "Offline Tables",
    offlinetables: "Offline Tables",
    kscreendesign: "Screens",
    screendesigns: "Screens",
    kproject: "Project",
    project: "Project"
  };

  private friendlyTreeFolderName = (name: string): string => {
    const key: string = (name || "").trim().toLowerCase();
    return BranchMergeComponent.FRIENDLY_FOLDER_NAMES[key] ?? name;
  };

  // Whole-entity resolution for redundant-xml entities (workflows etc.): resolve EVERY region of the
  // file — json and xml together — to the same side, so the two encodings can never diverge. Replaces
  // the per-region xml side-pick heuristic, which could silently leave json and xml inconsistent.
  handleResolveWholeFile = (filePath: string, useOurs: boolean) => {
    const { mergeResult } = this.state;
    const fileConflicts = this.getResultFileConflicts(mergeResult);
    const fileConflict = fileConflicts.find((c) => c.filePath === filePath);
    if (!fileConflict) return;

    fileConflict.conflicts.forEach((region) => {
      region.isResolved = true;
      region.resolvedContent = useOurs ? (region.ours ?? "") : (region.theirs ?? "");
    });
    fileConflict.isResolved = true;
    fileConflict.resolutionStrategy = useOurs ? "ours" : "theirs";

    const allConflictsResolved = this.checkAllConflictsResolved(fileConflicts);
    this.setState({ mergeResult, allConflictsResolved });
    KMainFunctions.displaySuccessNotification(`Workflow resolved using ${useOurs ? "target" : "source"}.`);
  };

  /**
   * Resolution coming from the Summary tab (EntityDiffCard). Non-screen conflicts are whole-entity,
   * so the chosen full-entity JSON is written onto every region of the file and the file is marked
   * resolved — the same shape the backend expects from a whole-file pick.
   */
  handleResolveEntityFromSummary = (filePath: string, resolved: boolean, resolvedContent?: string) => {
    if (!resolved || !resolvedContent) return;
    const { mergeResult } = this.state;
    const fileConflicts = this.getResultFileConflicts(mergeResult);
    const fileConflict = fileConflicts.find((c) => c.filePath === filePath);
    if (!fileConflict) return;

    fileConflict.conflicts.forEach((region) => {
      region.isResolved = true;
      region.resolvedContent = resolvedContent;
    });
    fileConflict.isResolved = true;
    fileConflict.resolutionStrategy = "manual";

    const allConflictsResolved = this.checkAllConflictsResolved(fileConflicts);
    this.setState({ mergeResult, allConflictsResolved });
    KMainFunctions.displaySuccessNotification("Conflict resolved.");
  };

  handleResolveConflict = async (useCurrentChanges: boolean) => {
    const { selectedConflicts } = this.state;

    if (!selectedConflicts) return;

    try {
      const filesToResolve = this.getFilesUnderPath(selectedConflicts[0].filePath);

      var { mergeResult } = this.state;
      filesToResolve?.forEach((filePath) => {
        const mergeConflict = this.getResultFileConflicts(mergeResult).find(
          (conflict) => conflict.filePath === filePath
        );
        if (mergeConflict) {
          // Mark file as resolved
          mergeConflict.isResolved = true;
          mergeConflict.resolutionStrategy = useCurrentChanges ? "ours" : "theirs";

          // Mark all regions in this file as resolved
          mergeConflict.conflicts.forEach((region) => {
            region.isResolved = true;
            region.resolvedContent = useCurrentChanges ? (region.ours ?? "") : (region.theirs ?? "");
          });
        }
        this.setState({ mergeResult });
      });

      // Update state with new mergeResult and check if all conflicts are resolved
      const allConflictsResolved = this.checkAllConflictsResolved(this.getResultFileConflicts(mergeResult));

      this.setState({
        selectedConflicts: null,
        mergeResult,
        allConflictsResolved
      });
    } catch (error) {
      console.error("Error resolving conflict:", error);
    }
  };

  handleResolveRegion = (filePath: string, regionIndex: number, useOurs: boolean) => {
    const { mergeResult } = this.state;
    const fileConflicts = this.getResultFileConflicts(mergeResult);
    if (fileConflicts.length === 0) return;

    const fileConflict = fileConflicts.find((conflict) => conflict.filePath === filePath);
    if (!fileConflict || !fileConflict.conflicts[regionIndex]) return;

    const region = fileConflict.conflicts[regionIndex];
    region.isResolved = true;
    region.resolvedContent = useOurs ? (region.ours ?? "") : (region.theirs ?? "");

    // Auto-resolve linked Xml regions when a Json region is resolved
    autoResolveLinkedXmlRegions(fileConflict, region);

    // Check if all regions in this file are resolved
    const allRegionsResolved = fileConflict.conflicts.every((r) => r.isResolved);
    if (allRegionsResolved) {
      fileConflict.isResolved = true;
      const resolvedModes = fileConflict.conflicts.map((r) =>
        (r.resolvedContent ?? "").trim() === (r.ours ?? "").trim()
          ? "ours"
          : (r.resolvedContent ?? "").trim() === (r.theirs ?? "").trim()
            ? "theirs"
            : "manual"
      );
      fileConflict.resolutionStrategy = resolvedModes.every((m) => m === "ours")
        ? "ours"
        : resolvedModes.every((m) => m === "theirs")
          ? "theirs"
          : "manual";
    }

    // Update state and check if all conflicts are resolved
    const allConflictsResolved = this.checkAllConflictsResolved(fileConflicts);
    const stats = this.getConflictResolutionStats(fileConflicts);

    this.setState({
      mergeResult,
      allConflictsResolved,
      manualResolvedDraft: this.getInitialManualResolvedDraft(region)
    });

    KMainFunctions.displaySuccessNotification(
      `Conflict region ${regionIndex + 1} resolved. ${allRegionsResolved ? "File fully resolved!" : ""} `
    );
  };

  private regionMode = (r: IConflictRegionDto): "ours" | "theirs" | "manual" => {
    const resolved: string = (r.resolvedContent ?? "").trim();
    if (resolved === (r.ours ?? "").trim()) return "ours";
    if (resolved === (r.theirs ?? "").trim()) return "theirs";
    return "manual";
  };

  private recomputeFileResolution = (fileConflict: IConflictDto): void => {
    const regions = fileConflict.conflicts ?? [];
    if (regions.length === 0 || !regions.every((r) => r.isResolved)) return;
    fileConflict.isResolved = true;
    const modes = regions.map(this.regionMode);
    if (modes.every((m) => m === "ours")) {
      fileConflict.resolutionStrategy = "ours";
    } else if (modes.every((m) => m === "theirs")) {
      fileConflict.resolutionStrategy = "theirs";
    } else {
      fileConflict.resolutionStrategy = "manual";
    }
  };

  // Confident suggestions to apply in bulk. Redundant-xml files resolve as one unit; other files
  // yield one action per unresolved region that has a suggestion.
  private collectSuggestionActions = (fileConflicts: IConflictDto[]): SuggestionAction[] => {
    const actions: SuggestionAction[] = [];
    for (const file of fileConflicts) {
      if (file.isResolved) continue;
      const regions = file.conflicts ?? [];
      if (hasRedundantXml(file.filePath)) {
        const suggestion = regions.map((r) => suggestRegionResolution(r)).find(Boolean);
        if (suggestion) actions.push({ filePath: file.filePath, whole: true, regionIndex: -1, side: suggestion.side });
        continue;
      }
      regions.forEach((region, index) => {
        if (region.isResolved) return;
        const suggestion = suggestRegionResolution(region);
        if (suggestion)
          actions.push({ filePath: file.filePath, whole: false, regionIndex: index, side: suggestion.side });
      });
    }
    return actions;
  };

  private getSuggestionCount = (fileConflicts: IConflictDto[]): number => {
    return this.collectSuggestionActions(fileConflicts).length;
  };

  private applyAllSuggestions = () => {
    const { mergeResult } = this.state;
    const fileConflicts = this.getResultFileConflicts(mergeResult);
    const actions = this.collectSuggestionActions(fileConflicts);
    if (actions.length === 0) return;

    for (const action of actions) {
      const file = fileConflicts.find((f) => f.filePath === action.filePath);
      if (!file) continue;
      const useOurs: boolean = action.side === "target";
      if (action.whole) {
        (file.conflicts ?? []).forEach((r) => {
          r.isResolved = true;
          r.resolvedContent = useOurs ? (r.ours ?? "") : (r.theirs ?? "");
        });
        file.isResolved = true;
        file.resolutionStrategy = useOurs ? "ours" : "theirs";
        continue;
      }
      const region = (file.conflicts ?? [])[action.regionIndex];
      if (!region) continue;
      region.isResolved = true;
      region.resolvedContent = useOurs ? (region.ours ?? "") : (region.theirs ?? "");
      autoResolveLinkedXmlRegions(file, region);
      this.recomputeFileResolution(file);
    }

    const allConflictsResolved = this.checkAllConflictsResolved(fileConflicts);
    this.setState({ mergeResult, allConflictsResolved });
    KMainFunctions.displaySuccessNotification(
      `Applied ${actions.length} suggestion${actions.length === 1 ? "" : "s"}.`
    );
  };

  handleResolveRegionWithBase = (filePath: string, regionIndex: number) => {
    const { mergeResult } = this.state;
    const fileConflicts = this.getResultFileConflicts(mergeResult);
    if (fileConflicts.length === 0) return;

    const fileConflict = fileConflicts.find((conflict) => conflict.filePath === filePath);
    if (!fileConflict || !fileConflict.conflicts[regionIndex]) return;

    const region = fileConflict.conflicts[regionIndex];
    const baseValue = (region as any)?.base || "";
    region.isResolved = true;
    region.resolvedContent = baseValue;

    // Auto-resolve linked Xml regions when a Json region is resolved with base
    autoResolveLinkedXmlRegions(fileConflict, region);

    const allRegionsResolved = fileConflict.conflicts.every((r) => r.isResolved);
    if (allRegionsResolved) {
      fileConflict.isResolved = true;
      fileConflict.resolutionStrategy = "manual";
    }

    const allConflictsResolved = this.checkAllConflictsResolved(fileConflicts);

    this.setState({
      mergeResult,
      allConflictsResolved,
      manualResolvedDraft: this.getInitialManualResolvedDraft(region)
    });

    KMainFunctions.displaySuccessNotification(
      `Base value applied for region ${regionIndex + 1}. ${allRegionsResolved ? "File fully resolved!" : ""}`
    );
  };

  private setActiveConflictFile = (filePath: string) => {
    // Land on the meaningful view, never raw JSON. Screen designs open on the canvas Preview;
    // workflows/APIs/scheduled jobs open on Resolve (its WorkflowConflictView is their diagram +
    // pick UI); other entities open on the semantic Summary (falling back to Resolve if unknown).
    let defaultSection: "resolve" | "preview" | "summary";
    if (this.isScreenDesignConflict(filePath)) {
      defaultSection = "preview";
    } else if (hasRedundantXml(filePath)) {
      defaultSection = "resolve";
    } else {
      const detectedType = entityTypeFromFilePath(filePath);
      defaultSection = detectedType !== null && detectedType !== 3 ? "summary" : "resolve";
    }
    this.setState(
      {
        activeConflictFilePath: filePath,
        activeConflictRegionIndex: 0,
        activeWorkspaceSection: defaultSection,
        showAdvancedActions: false,
        showAdvancedPreview: false,
        showRawValuePanels: false,
        previewLayout: "target"
      },
      () => {
        const activeFile = this.state.selectedConflicts?.find((f) => f.filePath === filePath);
        const firstRegion = activeFile?.conflicts?.[0];
        this.setState(
          { manualResolvedDraft: this.getInitialManualResolvedDraft(firstRegion) },
          this.refreshActiveConflictPreview
        );
      }
    );
  };

  private setActiveConflictRegion = (index: number) => {
    this.setState(
      {
        activeConflictRegionIndex: index,
        activeWorkspaceSection: "resolve",
        showAdvancedActions: false,
        showAdvancedPreview: false,
        showRawValuePanels: false,
        previewLayout: "target"
      },
      () => {
        const activeFile =
          this.state.selectedConflicts?.find((f) => f.filePath === this.state.activeConflictFilePath) ||
          this.state.selectedConflicts?.[0];
        const activeRegion = activeFile?.conflicts?.[index];
        this.setState(
          { manualResolvedDraft: this.getInitialManualResolvedDraft(activeRegion) },
          this.refreshActiveConflictPreview
        );
      }
    );
  };

  private handleManualDraftChange = (value?: string) => {
    this.setState({ manualResolvedDraft: value || "" });
  };

  private applyManualResolutionForActiveRegion = () => {
    const { mergeResult, activeConflictFilePath, activeConflictRegionIndex, manualResolvedDraft } = this.state;
    if (!activeConflictFilePath) return;
    const fileConflicts = this.getResultFileConflicts(mergeResult);
    if (fileConflicts.length === 0) return;

    const fileConflict = fileConflicts.find((conflict) => conflict.filePath === activeConflictFilePath);
    if (!fileConflict || !fileConflict.conflicts[activeConflictRegionIndex]) return;

    const region = fileConflict.conflicts[activeConflictRegionIndex];
    region.isResolved = true;
    region.resolvedContent = manualResolvedDraft;

    const allRegionsResolved = fileConflict.conflicts.every((r) => r.isResolved);
    if (allRegionsResolved) {
      fileConflict.isResolved = true;
      fileConflict.resolutionStrategy = "manual";
    }

    const allConflictsResolved = this.checkAllConflictsResolved(fileConflicts);
    this.setState({ mergeResult, allConflictsResolved });

    KMainFunctions.displaySuccessNotification(`Manual resolution applied to region ${activeConflictRegionIndex + 1}.`);
  };

  getFilesUnderPath = (path: string): string[] | undefined => {
    const { mergeResult } = this.state;
    if (!mergeResult?.conflictTree) return undefined;

    const getAllFilePaths = (tree: IBranchTreeNode[]): string[] => {
      const filePaths: string[] = [];

      for (const node of tree) {
        // Add file conflicts from current node if they exist
        if (node.fileConflicts && node.fileConflicts.length > 0) {
          const paths = node.fileConflicts
            .map((conflict) => conflict.filePath)
            .filter((filePath): filePath is string => filePath !== undefined);
          filePaths.push(...paths);
        }

        // Recursively process children
        if (node.children && node.children.length > 0) {
          filePaths.push(...getAllFilePaths(node.children));
        }
      }

      return filePaths;
    };

    return getAllFilePaths(mergeResult.conflictTree);
  };

  handleMergeRequest = (summary: string, description: string, targetBranch: string): IConflictDto[] => {
    this.props.setLoading(true);
    const { mergeData } = this.state;
    const targetBranchName = this.props.sd.branches?.find((branch) => branch.id === targetBranch)?.name ?? null;
    const nextMergeData = {
      ...(mergeData || ({} as IMergeRequestDto)),
      targetBranchId: targetBranch,
      sourceBranchId: this.props.sd.currentBranch?.id || "",
      mergeMessage: `${summary}\n${description}`
    } as IMergeRequestDto;

    const sourceBranchId = nextMergeData.sourceBranchId;
    const stagedPromise = sourceBranchId
      ? BranchingService.GetStagedEntities(sourceBranchId)
      : Promise.resolve(null as any);

    stagedPromise
      .then((stagedRes) => {
        const pendingStagedOpIds = this.extractOpIdsFromStagedEntities(stagedRes?.data || []);
        return { pendingStagedOpIds };
      })
      .then(({ pendingStagedOpIds }) => {
        const hasUnapprovedOps = pendingStagedOpIds.length > 0;
        if (hasUnapprovedOps) {
          this.setState({
            mergeData: nextMergeData,
            branchToMerge: targetBranchName,
            pendingStagedOpIds,
            showMergeToBranchModal: false,
            showChangesFoundModal: true,
            changesFoundCommitMessage: "",
            changesFoundInProgress: false
          });
          this.props.setLoading(false);
          return;
        }

        this.setState({ mergeData: nextMergeData }, () => {
          this.executeMergeRequest(nextMergeData, targetBranchName);
        });
      })
      .catch((error) => {
        // GetStagedEntities reports its own failure in a toast, and the steps after it only move
        // state around — so there is nothing here the user has not already been told.
        console.error("Error preparing merge:", error);
        this.props.setLoading(false);
      });

    return [];
  };

  handleCloseMergeToBranchModal = () => {
    this.setState({ showMergeToBranchModal: false });
    this.props.endMerge();
  };

  handleStartMerge = () => {
    this.setState({ showMergeToBranchModal: false });
  };

  getFileLanguage = (filePath: string): string => {
    const extension = filePath.split(".").pop()?.toLowerCase() || "";
    switch (extension) {
      case "ts":
      case "tsx":
        return "typescript";
      case "js":
      case "jsx":
        return "javascript";
      case "json":
        return "json";
      case "css":
        return "css";
      case "scss":
        return "scss";
      case "html":
        return "html";
      case "md":
        return "markdown";
      default:
        return "text";
    }
  };

  combineConflictRegions = (regions: IConflictRegionDto[]): { ours: string; theirs: string } => {
    const sortedRegions = [...regions].sort((a, b) => a.startLine - b.startLine);

    let combinedOurs = "";
    let combinedTheirs = "";

    sortedRegions.forEach((region, index) => {
      if (index > 0) {
        combinedOurs += "\n\n";
        combinedTheirs += "\n\n";
      }

      const header = `<<<<<<< Conflict Region ${index + 1} (Lines ${region.startLine}-${region.endLine}) >>>>>>>>>\n`;
      combinedOurs += header;
      combinedTheirs += header;

      const formattedOurs = region.ours
        .split("\n")
        .map((line) => `  ${line}`)
        .join("\n");
      const formattedTheirs = region.theirs
        .split("\n")
        .map((line) => `  ${line}`)
        .join("\n");

      combinedOurs += formattedOurs;
      combinedTheirs += formattedTheirs;

      // Add a footer for visual separation
      const footer = "\n=======================================================";
      combinedOurs += footer;
      combinedTheirs += footer;
    });

    return { ours: combinedOurs, theirs: combinedTheirs };
  };

  handleCloseChangesFoundModal = () => {
    this.setState({ showChangesFoundModal: false });
  };

  handleChangesFoundCommitMessageChange = (value: string) => {
    this.setState({ changesFoundCommitMessage: value });
  };

  handleIgnoreChangesAndMerge = () => {
    const { mergeData, pendingStagedOpIds } = this.state;

    this.props.setLoading(true);
    this.setState({ changesFoundInProgress: true });
    const sourceBranchId = mergeData?.sourceBranchId;
    const discardPromise =
      sourceBranchId && pendingStagedOpIds.length > 0
        ? BranchingService.DiscardStagedOps(sourceBranchId, pendingStagedOpIds)
        : Promise.resolve(null as any);

    discardPromise
      .then(() => {
        if (!mergeData) {
          this.props.setLoading(false);
          return;
        }

        this.setState({ showChangesFoundModal: false, pendingStagedOpIds: [] });
        this.executeMergeRequest(mergeData, this.state.branchToMerge);
      })
      .catch(() => {
        // Discard failed — the service already toasted the error. The dialog stays up with its
        // changes, and the spinner is released rather than left hanging over it.
        this.props.setLoading(false);
      })
      .finally(() => {
        this.setState({ changesFoundInProgress: false });
        if (!mergeData) {
          this.props.setLoading(false);
        }
      });
  };

  handleCommitAndMerge = () => {
    const { mergeData, pendingStagedOpIds } = this.state;
    const sourceBranchId = mergeData?.sourceBranchId;

    this.props.setLoading(true);
    this.setState({ changesFoundInProgress: true });
    // Real commit (not accept-only) so the source's committed snapshot advances — the merge-request
    // review/approve reads that snapshot, so an accepted-but-uncommitted change would otherwise be missed.
    // Use the commit message the user typed in the modal; fall back when it's blank.
    const commitMessage: string = (this.state.changesFoundCommitMessage || "").trim() || "Committed before merge";
    const commitPromise =
      sourceBranchId && pendingStagedOpIds.length > 0
        ? BranchingService.CommitStaged(sourceBranchId, commitMessage)
        : Promise.resolve(null as any);

    commitPromise
      .then(() => {
        if (!mergeData) {
          this.props.setLoading(false);
          return;
        }

        this.setState({ showChangesFoundModal: false, pendingStagedOpIds: [] });
        this.executeMergeRequest(mergeData, this.state.branchToMerge);
      })
      .catch(() => {
        // Commit failed (e.g. no permission to commit to the source) — the service already toasted the
        // error; just release the spinner so the modal doesn't hang, and don't proceed to merge.
        this.props.setLoading(false);
      })
      .finally(() => {
        this.setState({ changesFoundInProgress: false });
        if (!mergeData) {
          this.props.setLoading(false);
        }
      });
  };

  completeMerge(isComplete: boolean) {
    return () => {
      if (isComplete) {
        const { mergeResult } = this.state;

        // Guard against double-submit: ignore extra clicks while a completion request is in flight.
        if (this.state.isCompletingMerge) return;

        // Check if there are conflicts and if all conflicts are resolved
        const resolvedFileConflicts = this.getResultFileConflicts(mergeResult);
        const hasConflicts = resolvedFileConflicts.length > 0;

        if (hasConflicts && !this.state.allConflictsResolved) {
          const stats = this.getConflictResolutionStats(resolvedFileConflicts);
          KMainFunctions.displayWarningNotification(
            `Cannot complete merge! Please resolve all conflicts first. ${stats.resolved} of ${stats.total} files resolved.`
          );
          return;
        }

        if (hasConflicts) {
          const payloadValidation = this.validateResolvedConflictPayload(resolvedFileConflicts);
          if (!payloadValidation.isValid) {
            KMainFunctions.displayErrorNotification(
              payloadValidation.message || "Cannot complete merge due to invalid conflict payload."
            );
            return;
          }
        }

        this.setState({ isCompletingMerge: true });
        this.props.setLoading(true);
        const mergeData = {
          targetBranchId: this.state.mergeData?.targetBranchId || "",
          sourceBranchId: this.state.mergeData?.sourceBranchId || "",
          mergeMessage: this.state.mergeData?.mergeMessage || "",
          conflicts: hasConflicts ? this.getResultFileConflicts(this.state.mergeResult) : []
        } as IMergeRequestDto;

        const resolvePromise = this.props.reviewMode
          ? this.props.reviewMode.onResolve(mergeData)
          : this.props.sd.resolveMergeConflicts(mergeData);
        // Completing is the long half of a merge: one toast carries it from "Completing" to its
        // outcome, where the success and each failure used to raise a toast of their own.
        let completionToastId: ToastId | null = null;
        const work: Promise<IMergeResultDto | undefined> = (async (): Promise<IMergeResultDto | undefined> => {
          let result: IMergeResultDto | undefined;
          try {
            result = await resolvePromise;
          } catch (error: unknown) {
            // The service has already reported the failure in its own toast; this one steps aside
            // before the rejection reaches it, rather than say the same thing twice.
            if (completionToastId !== null) toastService.dismiss(completionToastId);
            throw error;
          }
          // Thrown here rather than by the service, so this one still belongs to the promise toast.
          const hasConflictContractMismatch: boolean =
            !!result?.hasConflicts && this.getResultFileConflicts(result).length === 0;
          if (hasConflictContractMismatch) {
            console.error(
              "Resolve merge contract mismatch: hasConflicts=true but conflictTree has no file conflicts.",
              result
            );
            throw new MergeContractMismatchError("Merge completion returned inconsistent conflict data. Please retry.");
          }
          return result;
        })();
        completionToastId = toastService.promise(work, {
          loading: "Completing the merge…",
          success: (result: IMergeResultDto | undefined): string => {
            if (result?.mergeRequestCreated) {
              return "Merge request submitted for approval. The branch owner will review it.";
            }
            return hasConflicts
              ? "Merge completed successfully! All conflicts have been resolved and changes have been merged."
              : "Merge completed successfully! All auto-merged changes have been applied.";
          },
          error: (reason: unknown): string => describeMergeFailure(reason, "Failed to complete merge")
        });
        work
          .then(() => {
            if (this.isUnmounted) return;
            this.props.setLoading(false);
            this.setState({ showMergeToBranchModal: false, isCompletingMerge: false });
            this.props.endMerge({
              completed: true,
              targetBranchId: this.state.mergeData?.targetBranchId
            });
          })
          .catch((error) => {
            if (this.isUnmounted) return;
            console.error("Error during merge:", error);
            this.props.setLoading(false);
            this.setState({ isCompletingMerge: false });
          });
      } else if (this.state.mergeResult) {
        // Review mode: approve only COMPUTED the conflicts (nothing applied server-side), so there is
        // no live merge to cancel — just exit the resolver back to the request.
        if (this.props.reviewMode) {
          this.setState({ showMergeToBranchModal: false });
          this.props.endMerge({ completed: false });
          return;
        }
        this.props.setLoading(true);

        this.props.sd
          .cancelMerge(this.state.mergeResult)
          .then(() => {
            if (this.isUnmounted) return;
            this.props.setLoading(false);
            KMainFunctions.displayInfoNotification("Merge cancelled. All temporary changes have been discarded.");
            this.setState({ showMergeToBranchModal: false });
            this.props.endMerge({ completed: false });
          })
          .catch((error) => {
            if (this.isUnmounted) return;
            // The service has already reported the failure in its own toast; saying it again here
            // would put two notifications on screen for one cancel.
            console.error("Error during cancel merge:", error);
            this.props.setLoading(false);
            this.setState({ showMergeToBranchModal: false });
            this.props.endMerge({ completed: false });
          });
      }
    };
  }

  _calculateTreeNode = (changeTree: IBranchTreeNode[] | undefined, includeChangesOnly = false): ConflictTreeItem[] => {
    if (!changeTree) return [];

    return changeTree
      .map((item) => {
        const { id, name, type, changeStatus, children, fileConflicts } = item;
        const isLeaf = !children || children.length === 0;

        const key = id || fileConflicts?.[0]?.filePath || "";
        const title = name || fileConflicts?.[0]?.filePath || "";

        const isResolved = fileConflicts?.every((conflict) => conflict.isResolved) ?? false;
        const hasConflicts = fileConflicts && fileConflicts.length > 0;

        const effectiveChangeStatus = changeStatus || (item as any).changeType;
        let dotKind: string | null = null;
        switch ((effectiveChangeStatus || "").toLowerCase()) {
          case "modified":
          case "m":
            dotKind = "modified";
            break;
          case "added":
          case "a":
            dotKind = "added";
            break;
          case "deleted":
          case "d":
            dotKind = "deleted";
            break;
          case "conflict":
          case "c":
            dotKind = "conflict";
            break;
          default:
            dotKind = null;
        }

        // Recursively process children first
        const processedChildren = isLeaf ? [] : this._calculateTreeNode(children, includeChangesOnly);

        const combinedChildren = processedChildren;

        const displayTitle = hasConflicts
          ? this.resolveTreeDisplayName(title, fileConflicts)
          : this.friendlyTreeFolderName(title);
        const titleNode = (
          <span className={`branch-merge__tree-row ${OVERFLOW_REVEAL_HOST_CLASS}`}>
            {dotKind && <span className="branch-merge__tree-kind" data-kind={dotKind} />}
            <span className="branch-merge__tree-name">
              <KuikaOverflowText>{displayTitle}</KuikaOverflowText>
            </span>
            {hasConflicts && (
              <span className="branch-merge__tree-status" data-status={isResolved ? "resolved" : "pending"}>
                {isResolved ? (
                  <KuikaSvg name="check" width={11} height={11} />
                ) : (
                  <KuikaSvg name="alert-triangle" width={12} height={12} />
                )}
              </span>
            )}
          </span>
        );

        // Create node compatible with Ant Design DirectoryTree
        return {
          key,
          title: titleNode,
          isLeaf: combinedChildren.length === 0,
          type,
          children: combinedChildren.length > 0 ? combinedChildren : undefined,
          conflicts: fileConflicts
        };
      })
      .filter((item) => {
        // Keep items with direct or descendant conflicts.
        const hasDirectConflicts = item.conflicts && item.conflicts.length > 0;
        const hasChildConflicts = item.children && item.children.length > 0;
        if (includeChangesOnly) {
          return !!(hasDirectConflicts || hasChildConflicts);
        }
        return !!(hasDirectConflicts || hasChildConflicts);
      });
  };

  render() {
    const {
      selectedConflicts,
      expandedKeys,
      showMergeToBranchModal,
      showChangesFoundModal,
      mergeResult,
      allConflictsResolved,
      currentBranch,
      branchToMerge,
      selectedKey,
      selectedChanges,
      activeConflictFilePath,
      activeConflictRegionIndex,
      manualResolvedDraft,
      previewLayout,
      previewFullscreen,
      showRawValuePanels,
      showAdvancedActions,
      showAdvancedPreview
    } = this.state;
    const fileConflicts = this.getResultFileConflicts(mergeResult);
    const hasConflicts = fileConflicts.length > 0;
    const conflictTree = this._calculateTreeNode(mergeResult?.conflictTree, !hasConflicts);
    const branchOptions = sortBranchesMasterFirst(this.props.sd.branches)
      .filter((branch) => branch.id !== this.props.sd.currentBranch?.id)
      .map((branch) => ({
        value: branch.id,
        label: branch.name,
        testId: `mergeTargetBranchOption-${branch.name}`,
        canMergeInto: branch.permissions?.canMergeInto,
        isProtected: branch.isProtected
      }));
    const { DirectoryTree } = Tree;

    const editorOptions = {
      readOnly: true,
      renderSideBySide: true,
      minimap: { enabled: false },
      lineNumbers: "on" as const,
      scrollBeyondLastLine: false,
      wordWrap: "on" as const,
      renderWhitespace: "all" as const,
      contextmenu: false,
      fontSize: 13
    };

    // Until a merge has actually run (mergeResult exists) the tab shows the inline
    // setup panel rather than the conflict workspace.
    const showSetup = !mergeResult;

    return (
      <div className="branch-merge">
        <div className="branch-merge__content">
          {/* The tree rail is the navigator of the REVIEW stage — before Start merge there is
              nothing to navigate (conflictTree is computed by the merge run), so an empty labeled
              column would just sit beside the setup form. Render it only once the merge ran. */}
          {!showSetup && (
            <div className="branch-merge__files-list">
              <div className="branch-merge__header">
                <div className="branch-merge__header-content">
                  <h3>{hasConflicts ? "Conflicts" : "Merge Changes"}</h3>
                  {(() => {
                    const stats = this.getConflictResolutionStats(fileConflicts);
                    if (stats.total === 0) return null;
                    const percentage = stats.total > 0 ? Math.round((stats.resolved / stats.total) * 100) : 0;
                    return (
                      <div className="branch-merge__progress-container">
                        <span className="branch-merge__progress-text">
                          {stats.resolved} of {stats.total} resolved ({percentage}%)
                        </span>
                        <div className="branch-merge__progress-bar">
                          <div
                            className={`branch-merge__progress-fill${percentage === 100 ? " branch-merge__progress-fill--complete" : ""}`}
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
              <div className="branch-merge__files-tree">
                <DirectoryTree
                  showIcon={false}
                  blockNode
                  treeData={conflictTree?.map((item) => ({ ...item, key: item.key ?? String(item.title) }))}
                  onSelect={this.handleSelect}
                  expandedKeys={expandedKeys}
                  onExpand={this.handleExpand}
                />
              </div>
            </div>
          )}

          <div className="branch-merge__details-panel">
            {selectedConflicts && selectedConflicts.length > 0 ? (
              <div className="branch-merge__conflict-details">
                {(() => {
                  const activeFile =
                    selectedConflicts.find((f) => f.filePath === activeConflictFilePath) || selectedConflicts[0];
                  const activeFileDisplayName = this.resolveTreeDisplayName(
                    activeFile?.filePath || this.state.selectedKey || "",
                    activeFile ? [activeFile] : undefined
                  );
                  const activeRegion = activeFile?.conflicts?.[activeConflictRegionIndex] || activeFile?.conflicts?.[0];
                  const baseSnapshotText = (activeRegion as any)?.baseSnapshot || (activeRegion as any)?.base;
                  const targetSnapshotText = (activeRegion as any)?.oursSnapshot || activeRegion?.ours;
                  const sourceSnapshotText = (activeRegion as any)?.theirsSnapshot || activeRegion?.theirs;
                  const conflictPath = (activeRegion as any)?.conflictPath;
                  const baseFocusValue = this.formatConflictFocusValue(
                    this.getSnapshotValueAtConflictPath(baseSnapshotText, conflictPath)
                  );
                  const targetFocusValue = this.formatConflictFocusValue(
                    this.getSnapshotValueAtConflictPath(targetSnapshotText, conflictPath)
                  );
                  const sourceFocusValue = this.formatConflictFocusValue(
                    this.getSnapshotValueAtConflictPath(sourceSnapshotText, conflictPath)
                  );
                  const baseConflictPartText = this.getRegionSideValue(activeRegion, "base");
                  const targetConflictPartText = this.getRegionSideValue(activeRegion, "ours");
                  const sourceConflictPartText = this.getRegionSideValue(activeRegion, "theirs");
                  const conflictType = (activeRegion as any)?.conflictType || "edit-vs-edit";
                  const conflictGuidance = this.getConflictTypeGuidance(conflictType);
                  const resolutionLabel = this.getRegionResolutionLabel(activeRegion);
                  const activeSuggestion = suggestRegionResolution(activeRegion);
                  const suggestionBranch =
                    activeSuggestion?.side === "target" ? mergeResult?.targetBranch : mergeResult?.baseBranch;
                  const shouldShowPreviewDrawer = this.shouldRenderPreviewSection(activeFile.filePath, activeRegion);
                  const { activeWorkspaceSection } = this.state;

                  return (
                    <>
                      <div className="branch-merge__conflict-header">
                        <div className="branch-merge__conflict-info">
                          <h3>{activeFileDisplayName}</h3>
                          {activeRegion && (
                            <span
                              className="branch-merge__conflict-status"
                              data-status={activeRegion.isResolved ? "resolved" : "pending"}
                            >
                              {activeRegion.isResolved ? "Resolved" : "Pending"}
                            </span>
                          )}
                        </div>
                        <div className="branch-merge__conflict-nav">
                          {(activeFile?.conflicts?.length || 0) > 1 && !hasRedundantXml(activeFile.filePath) && (
                            <div className="branch-merge__region-stepper">
                              <KuikaButton
                                type="text"
                                size="sm"
                                isIcon
                                disabled={activeConflictRegionIndex <= 0}
                                onClick={() => this.setActiveConflictRegion(activeConflictRegionIndex - 1)}
                              >
                                <KuikaSvg name="chevron-left" type="arrows" width={15} height={15} />
                              </KuikaButton>
                              <span className="branch-merge__region-stepper-label">
                                Region {activeConflictRegionIndex + 1} of {activeFile?.conflicts?.length || 0}
                              </span>
                              <KuikaButton
                                type="text"
                                size="sm"
                                isIcon
                                disabled={activeConflictRegionIndex >= (activeFile?.conflicts?.length || 1) - 1}
                                onClick={() => this.setActiveConflictRegion(activeConflictRegionIndex + 1)}
                              >
                                <KuikaSvg name="chevron-right" type="arrows" width={15} height={15} />
                              </KuikaButton>
                            </div>
                          )}
                          <KuikaButton type="secondary" size="sm" onClick={this.jumpToNextUnresolvedRegion}>
                            Next unresolved
                          </KuikaButton>
                        </div>
                      </div>

                      <div className="branch-merge__workspace-main">
                        {activeRegion && (
                          <div className="branch-merge__workspace-section-tabs">
                            <KuikaTabs
                              size="sm"
                              value={activeWorkspaceSection}
                              onChange={(v) =>
                                this.setState({
                                  activeWorkspaceSection: v as "resolve" | "resolved" | "preview" | "summary"
                                })
                              }
                              options={[
                                { value: "resolve", label: "Resolve" },
                                { value: "resolved", label: "Resolved Output" },
                                ...(shouldShowPreviewDrawer ? [{ value: "preview", label: "Preview" }] : []),
                                ...(entityTypeFromFilePath(activeFile.filePath) !== null &&
                                entityTypeFromFilePath(activeFile.filePath) !== 3
                                  ? [{ value: "summary", label: "Summary" }]
                                  : [])
                              ]}
                            />
                          </div>
                        )}

                        {activeRegion ? (
                          <>
                            {activeFile.conflicts &&
                              activeFile.conflicts.length > 1 &&
                              !hasRedundantXml(activeFile.filePath) && (
                                <div className="branch-merge__region-nav">
                                  <div className="branch-merge__region-nav-title">
                                    {activeFile.conflicts.length} changes in this screen
                                  </div>
                                  <ul className="branch-merge__region-nav-list">
                                    {activeFile.conflicts.map((region, regionIndex) => {
                                      if (hasRedundantXml(activeFile.filePath) && isXmlRegion(region)) return null;
                                      const isActiveRegion = regionIndex === activeConflictRegionIndex;
                                      const isResolvedRegion = !!region.isResolved;
                                      return (
                                        <li key={`region-nav-${regionIndex}`}>
                                          <button
                                            type="button"
                                            className="branch-merge__region-nav-item"
                                            data-active={isActiveRegion || undefined}
                                            data-resolved={isResolvedRegion || undefined}
                                            onClick={() => this.setActiveConflictRegion(regionIndex)}
                                          >
                                            <span className="branch-merge__region-nav-status" aria-hidden="true">
                                              {isResolvedRegion ? (
                                                <KuikaSvg name="check" width={12} height={12} />
                                              ) : (
                                                <span className="branch-merge__region-nav-dot" />
                                              )}
                                            </span>
                                            <span className="branch-merge__region-nav-label">
                                              {this.getFriendlyConflictPathLabel(region, regionIndex)}
                                            </span>
                                          </button>
                                        </li>
                                      );
                                    })}
                                  </ul>
                                </div>
                              )}
                            {activeWorkspaceSection === "resolve" && hasRedundantXml(activeFile.filePath) && (
                              <WorkflowConflictView
                                fileConflict={activeFile}
                                sourceLabel={mergeResult?.baseBranch ?? "source"}
                                targetLabel={mergeResult?.targetBranch ?? "target"}
                                resolved={!!activeFile.isResolved}
                                resolutionStrategy={activeFile.resolutionStrategy}
                                onUseSource={() => this.handleResolveWholeFile(activeFile.filePath, false)}
                                onUseTarget={() => this.handleResolveWholeFile(activeFile.filePath, true)}
                              />
                            )}
                            {activeWorkspaceSection === "resolve" && !hasRedundantXml(activeFile.filePath) && (
                              <div className="branch-merge__resolve">
                                {activeSuggestion && !activeRegion?.isResolved && (
                                  <div className="branch-merge__suggestion">
                                    <KuikaSvg name="star" width={15} height={15} />
                                    <span className="branch-merge__suggestion-main">
                                      Suggested: use {activeSuggestion.side}
                                      {suggestionBranch ? ` (${suggestionBranch})` : ""}
                                    </span>
                                    <span className="branch-merge__suggestion-reason">{activeSuggestion.reason}</span>
                                  </div>
                                )}
                                <div className="branch-merge__choices">
                                  <div
                                    className="branch-merge__choice"
                                    data-selected={resolutionLabel === "target" || undefined}
                                  >
                                    <button
                                      type="button"
                                      className="branch-merge__choice-head"
                                      onClick={() =>
                                        this.handleResolveRegion(activeFile.filePath, activeConflictRegionIndex, true)
                                      }
                                    >
                                      <span className="branch-merge__choice-name">
                                        Target
                                        {mergeResult?.targetBranch && (
                                          <em className="branch-merge__choice-branch">{mergeResult.targetBranch}</em>
                                        )}
                                      </span>
                                      <span className="branch-merge__choice-radio" aria-hidden="true">
                                        {resolutionLabel === "target" && (
                                          <KuikaSvg name="check" width={12} height={12} />
                                        )}
                                      </span>
                                    </button>
                                    {activeRegion?.oursMissing && (
                                      <div className="branch-merge__side-state">Deleted on target branch</div>
                                    )}
                                    <div className="branch-merge__choice-editor">
                                      <Editor
                                        height="200px"
                                        language={this.getFileLanguage(activeFile.filePath)}
                                        theme={CODE_EDITOR_THEME}
                                        beforeMount={this.handleEditorBeforeMount}
                                        value={targetConflictPartText}
                                        options={{
                                          readOnly: true,
                                          minimap: { enabled: false },
                                          scrollBeyondLastLine: false,
                                          wordWrap: "on",
                                          fontSize: 12
                                        }}
                                        loading={<div>Loading...</div>}
                                      />
                                    </div>
                                    <KuikaTooltip content={conflictGuidance.targetAction} position="top">
                                      <KuikaButton
                                        type={resolutionLabel === "target" ? "primary" : "secondary"}
                                        size="md"
                                        onClick={() =>
                                          this.handleResolveRegion(activeFile.filePath, activeConflictRegionIndex, true)
                                        }
                                        className="branch-merge__resolve-choice"
                                      >
                                        <span className="branch-merge__complete-label">
                                          {resolutionLabel === "target" && (
                                            <KuikaSvg name="check" width={15} height={15} />
                                          )}
                                          {activeSuggestion?.side === "target" && !activeRegion?.isResolved && (
                                            <KuikaSvg name="star" width={14} height={14} />
                                          )}
                                          {resolutionLabel === "target" ? "Using target" : "Use target"}
                                        </span>
                                      </KuikaButton>
                                    </KuikaTooltip>
                                  </div>

                                  <div
                                    className="branch-merge__choice"
                                    data-selected={resolutionLabel === "source" || undefined}
                                  >
                                    <button
                                      type="button"
                                      className="branch-merge__choice-head"
                                      onClick={() =>
                                        this.handleResolveRegion(activeFile.filePath, activeConflictRegionIndex, false)
                                      }
                                    >
                                      <span className="branch-merge__choice-name">
                                        Source
                                        {mergeResult?.baseBranch && (
                                          <em className="branch-merge__choice-branch">{mergeResult.baseBranch}</em>
                                        )}
                                      </span>
                                      <span className="branch-merge__choice-radio" aria-hidden="true">
                                        {resolutionLabel === "source" && (
                                          <KuikaSvg name="check" width={12} height={12} />
                                        )}
                                      </span>
                                    </button>
                                    {activeRegion?.theirsMissing && (
                                      <div className="branch-merge__side-state">Deleted on source branch</div>
                                    )}
                                    <div className="branch-merge__choice-editor">
                                      <Editor
                                        height="200px"
                                        language={this.getFileLanguage(activeFile.filePath)}
                                        theme={CODE_EDITOR_THEME}
                                        beforeMount={this.handleEditorBeforeMount}
                                        value={sourceConflictPartText}
                                        options={{
                                          readOnly: true,
                                          minimap: { enabled: false },
                                          scrollBeyondLastLine: false,
                                          wordWrap: "on",
                                          fontSize: 12
                                        }}
                                        loading={<div>Loading...</div>}
                                      />
                                    </div>
                                    <KuikaTooltip content={conflictGuidance.sourceAction} position="top">
                                      <KuikaButton
                                        type={resolutionLabel === "source" ? "primary" : "secondary"}
                                        size="md"
                                        onClick={() =>
                                          this.handleResolveRegion(
                                            activeFile.filePath,
                                            activeConflictRegionIndex,
                                            false
                                          )
                                        }
                                        className="branch-merge__resolve-choice"
                                      >
                                        <span className="branch-merge__complete-label">
                                          {resolutionLabel === "source" && (
                                            <KuikaSvg name="check" width={15} height={15} />
                                          )}
                                          {activeSuggestion?.side === "source" && !activeRegion?.isResolved && (
                                            <KuikaSvg name="star" width={14} height={14} />
                                          )}
                                          {resolutionLabel === "source" ? "Using source" : "Use source"}
                                        </span>
                                      </KuikaButton>
                                    </KuikaTooltip>
                                  </div>
                                </div>

                                <div className="branch-merge__resolve-more">
                                  <KuikaButton
                                    type="text"
                                    size="sm"
                                    onClick={() =>
                                      this.setState((prev) => ({ showAdvancedActions: !prev.showAdvancedActions }))
                                    }
                                  >
                                    <span className="branch-merge__complete-label">
                                      <KuikaSvg
                                        name="chevron-down"
                                        type="arrows"
                                        width={14}
                                        height={14}
                                        className={`branch-merge__chevron${showAdvancedActions ? " branch-merge__chevron--open" : ""}`}
                                      />
                                      More options
                                    </span>
                                  </KuikaButton>
                                  {showAdvancedActions && (
                                    <div className="branch-merge__resolve-more-panel">
                                      <div className="branch-merge__resolve-more-actions">
                                        {!this.isScreenDesignConflict(activeFile.filePath) && (
                                          <KuikaButton
                                            type="secondary"
                                            color="neutral"
                                            size="sm"
                                            onClick={this.applyManualResolutionForActiveRegion}
                                          >
                                            Edit manually
                                          </KuikaButton>
                                        )}
                                        {!activeRegion?.baseMissing && (
                                          <KuikaButton
                                            type="secondary"
                                            color="neutral"
                                            size="sm"
                                            onClick={() =>
                                              this.handleResolveRegionWithBase(
                                                activeFile.filePath,
                                                activeConflictRegionIndex
                                              )
                                            }
                                          >
                                            Keep base value
                                          </KuikaButton>
                                        )}
                                        <KuikaButton
                                          type="secondary"
                                          color="neutral"
                                          size="sm"
                                          onClick={() => this.handleResolveConflict(true)}
                                        >
                                          Use target for all files
                                        </KuikaButton>
                                        <KuikaButton
                                          type="secondary"
                                          color="neutral"
                                          size="sm"
                                          onClick={() => this.handleResolveConflict(false)}
                                        >
                                          Use source for all files
                                        </KuikaButton>
                                      </div>
                                      {!activeRegion?.baseMissing && (
                                        <div className="branch-merge__choice-editor branch-merge__choice-editor--base">
                                          <div className="branch-merge__choice-name branch-merge__choice-name--base">
                                            Base value
                                          </div>
                                          <Editor
                                            height="120px"
                                            language={this.getFileLanguage(activeFile.filePath)}
                                            theme={CODE_EDITOR_THEME}
                                            beforeMount={this.handleEditorBeforeMount}
                                            value={baseConflictPartText}
                                            options={{
                                              readOnly: true,
                                              minimap: { enabled: false },
                                              scrollBeyondLastLine: false,
                                              wordWrap: "on",
                                              fontSize: 12
                                            }}
                                            loading={<div>Loading...</div>}
                                          />
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}

                            {activeWorkspaceSection === "resolved" &&
                              (() => {
                                // A screen design is a structured component tree — hand-editing its JSON
                                // here risks corrupting the tree that feeds code generation. Show the
                                // merged result read-only for screens; keep it editable only for
                                // code-like entities (actions, datasources) where that is expected.
                                const readOnlyResult = this.isScreenDesignConflict(activeFile.filePath);
                                return (
                                  <div className="branch-merge__workspace-editor branch-merge__workspace-editor--resolved">
                                    <div className="branch-merge__workspace-label">
                                      Resolved Output
                                      {readOnlyResult && (
                                        <span className="branch-merge__workspace-label-hint"> (read-only preview)</span>
                                      )}
                                    </div>
                                    <Editor
                                      height="65vh"
                                      language={this.getFileLanguage(activeFile.filePath)}
                                      theme={CODE_EDITOR_THEME}
                                      beforeMount={this.handleEditorBeforeMount}
                                      value={manualResolvedDraft}
                                      onChange={readOnlyResult ? undefined : this.handleManualDraftChange}
                                      options={{
                                        readOnly: readOnlyResult,
                                        minimap: { enabled: false },
                                        scrollBeyondLastLine: false,
                                        wordWrap: "on",
                                        fontSize: 12
                                      }}
                                      loading={<div>Loading...</div>}
                                    />
                                  </div>
                                );
                              })()}

                            {activeWorkspaceSection === "preview" && shouldShowPreviewDrawer && (
                              <div
                                className={`branch-merge__preview-section${previewFullscreen ? " branch-merge__preview-section--fullscreen" : ""}`}
                              >
                                <div className="branch-merge__workspace-label">
                                  Preview • {this.getFriendlyConflictPathLabel(activeRegion, activeConflictRegionIndex)}
                                </div>
                                <div className="branch-merge__preview-resolve">
                                  <span className="branch-merge__preview-resolve-hint">
                                    {resolutionLabel === "target" && "Keeping the target version of this change."}
                                    {resolutionLabel === "source" && "Keeping the source version of this change."}
                                    {resolutionLabel !== "target" &&
                                      resolutionLabel !== "source" &&
                                      "Compare the versions below, then choose which to keep."}
                                  </span>
                                  <div className="branch-merge__preview-resolve-buttons">
                                    <KuikaButton
                                      type={resolutionLabel === "target" ? "primary" : "secondary"}
                                      size="sm"
                                      onClick={() =>
                                        this.handleResolveRegion(activeFile.filePath, activeConflictRegionIndex, true)
                                      }
                                    >
                                      <span className="branch-merge__complete-label">
                                        {resolutionLabel === "target" && (
                                          <KuikaSvg name="check" width={14} height={14} />
                                        )}
                                        {resolutionLabel === "target" ? "Using target" : "Use target"}
                                        {mergeResult?.targetBranch && ` (${mergeResult.targetBranch})`}
                                      </span>
                                    </KuikaButton>
                                    <KuikaButton
                                      type={resolutionLabel === "source" ? "primary" : "secondary"}
                                      size="sm"
                                      onClick={() =>
                                        this.handleResolveRegion(activeFile.filePath, activeConflictRegionIndex, false)
                                      }
                                    >
                                      <span className="branch-merge__complete-label">
                                        {resolutionLabel === "source" && (
                                          <KuikaSvg name="check" width={14} height={14} />
                                        )}
                                        {resolutionLabel === "source" ? "Using source" : "Use source"}
                                        {mergeResult?.baseBranch && ` (${mergeResult.baseBranch})`}
                                      </span>
                                    </KuikaButton>
                                  </div>
                                </div>
                                <div className="branch-merge__preview-layout-switch">
                                  <KuikaButton
                                    size="sm"
                                    type={previewLayout === "target" ? "primary" : "secondary"}
                                    onClick={() => this.setPreviewLayout("target")}
                                  >
                                    Focus Target
                                  </KuikaButton>
                                  <KuikaButton
                                    size="sm"
                                    type={showAdvancedPreview ? "primary" : "secondary"}
                                    onClick={() =>
                                      this.setState((prev) => ({
                                        showAdvancedPreview: !prev.showAdvancedPreview,
                                        previewLayout: prev.showAdvancedPreview ? "target" : prev.previewLayout,
                                        showRawValuePanels: prev.showAdvancedPreview ? false : prev.showRawValuePanels
                                      }))
                                    }
                                  >
                                    {showAdvancedPreview ? "Hide Advanced Preview" : "Advanced Preview"}
                                  </KuikaButton>
                                  <KuikaButton
                                    size="sm"
                                    type={previewFullscreen ? "primary" : "secondary"}
                                    onClick={this.togglePreviewFullscreen}
                                  >
                                    <span className="branch-merge__complete-label">
                                      <KuikaSvg
                                        name={previewFullscreen ? "maximize-off" : "maximize"}
                                        width={14}
                                        height={14}
                                      />
                                      {previewFullscreen ? "Exit fullscreen" : "Fullscreen"}
                                    </span>
                                  </KuikaButton>
                                  {showAdvancedPreview && (
                                    <>
                                      <KuikaButton
                                        size="sm"
                                        type={showRawValuePanels ? "primary" : "secondary"}
                                        onClick={() =>
                                          this.setState((prev) => ({
                                            showRawValuePanels: !prev.showRawValuePanels
                                          }))
                                        }
                                      >
                                        {showRawValuePanels ? "Hide Focus" : "Show Focus"}
                                      </KuikaButton>
                                      <KuikaButton
                                        size="sm"
                                        type={previewLayout === "source" ? "primary" : "secondary"}
                                        onClick={() => this.setPreviewLayout("source")}
                                      >
                                        Focus Source
                                      </KuikaButton>
                                      {!activeRegion?.baseMissing && (
                                        <KuikaButton
                                          size="sm"
                                          type={previewLayout === "base" ? "primary" : "secondary"}
                                          onClick={() => this.setPreviewLayout("base")}
                                        >
                                          Focus Base
                                        </KuikaButton>
                                      )}
                                      <KuikaButton
                                        size="sm"
                                        type={previewLayout === "split" ? "primary" : "secondary"}
                                        onClick={() => this.setPreviewLayout("split")}
                                      >
                                        {activeRegion?.baseMissing ? "Split 2-way" : "Split 3-way"}
                                      </KuikaButton>
                                    </>
                                  )}
                                </div>

                                {showAdvancedPreview && showRawValuePanels && (
                                  <div className="branch-merge__conflict-focus">
                                    <div className="branch-merge__conflict-focus-header">
                                      <span>Conflict Focus</span>
                                      <span className="branch-merge__conflict-focus-path" title={conflictPath || "/"}>
                                        {this.getFriendlyConflictPathLabel(activeRegion, activeConflictRegionIndex)}
                                      </span>
                                    </div>
                                    <div className="branch-merge__conflict-focus-grid">
                                      {!activeRegion?.baseMissing && (
                                        <div className="branch-merge__conflict-focus-card is-base">
                                          <div className="branch-merge__conflict-focus-title">Base</div>
                                          <pre>{baseFocusValue}</pre>
                                        </div>
                                      )}
                                      <div className="branch-merge__conflict-focus-card is-target">
                                        <div className="branch-merge__conflict-focus-title">Target</div>
                                        <pre>{targetFocusValue}</pre>
                                      </div>
                                      <div className="branch-merge__conflict-focus-card is-source">
                                        <div className="branch-merge__conflict-focus-title">Source</div>
                                        <pre>{sourceFocusValue}</pre>
                                      </div>
                                    </div>
                                  </div>
                                )}

                                <div
                                  className={`branch-merge__preview-grid branch-merge__preview-grid--${previewLayout === "split" && activeRegion?.baseMissing ? "split-no-base" : previewLayout}`}
                                >
                                  {!activeRegion?.baseMissing && (
                                    <div className="branch-merge__preview-card is-base">
                                      <div className="branch-merge__workspace-label">Base Preview</div>
                                      {this.renderPreviewDiagnostics("base")}
                                      {this.normalizeDesignSnapshot(baseSnapshotText) ? (
                                        <div className="branch-merge__preview-frame-shell">
                                          <iframe
                                            ref={this.basePreviewFrameRef}
                                            title="Base preview"
                                            src="/agent"
                                            className="branch-merge__preview-frame"
                                            onLoad={this.refreshActiveConflictPreview}
                                          />
                                        </div>
                                      ) : (
                                        <div className="branch-merge__preview-empty">Preview unavailable for base.</div>
                                      )}
                                    </div>
                                  )}
                                  <div className="branch-merge__preview-card is-target">
                                    <div className="branch-merge__workspace-label">
                                      Target Preview ({mergeResult?.targetBranch})
                                    </div>
                                    {this.renderPreviewDiagnostics("target")}
                                    {this.normalizeDesignSnapshot(targetSnapshotText) ? (
                                      <div className="branch-merge__preview-frame-shell">
                                        <iframe
                                          ref={this.targetPreviewFrameRef}
                                          title="Target preview"
                                          src="/agent"
                                          className="branch-merge__preview-frame"
                                          onLoad={this.refreshActiveConflictPreview}
                                        />
                                      </div>
                                    ) : (
                                      <div className="branch-merge__preview-empty">
                                        Preview unavailable for this target region.
                                      </div>
                                    )}
                                  </div>
                                  <div className="branch-merge__preview-card is-source">
                                    <div className="branch-merge__workspace-label">
                                      Source Preview ({mergeResult?.baseBranch})
                                    </div>
                                    {this.renderPreviewDiagnostics("source")}
                                    {this.normalizeDesignSnapshot(sourceSnapshotText) ? (
                                      <div className="branch-merge__preview-frame-shell">
                                        <iframe
                                          ref={this.sourcePreviewFrameRef}
                                          title="Source preview"
                                          src="/agent"
                                          className="branch-merge__preview-frame"
                                          onLoad={this.refreshActiveConflictPreview}
                                        />
                                      </div>
                                    ) : (
                                      <div className="branch-merge__preview-empty">
                                        Preview unavailable for this source region.
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}

                            {activeWorkspaceSection === "summary" &&
                              (() => {
                                const detectedType = entityTypeFromFilePath(activeFile.filePath);
                                if (detectedType == null || detectedType === 3) return null;
                                return (
                                  <div className="branch-merge__summary-pane">
                                    <EntityDiffCard
                                      entityType={detectedType}
                                      mode="merge"
                                      baseSnapshot={activeRegion?.baseSnapshot ?? (activeRegion as any)?.base}
                                      oursSnapshot={activeRegion?.oursSnapshot ?? (activeRegion as any)?.ours}
                                      theirsSnapshot={activeRegion?.theirsSnapshot ?? (activeRegion as any)?.theirs}
                                      targetLabel={mergeResult?.targetBranch}
                                      sourceLabel={mergeResult?.baseBranch}
                                      onResolutionChange={(resolved, resolvedContent) =>
                                        this.handleResolveEntityFromSummary(
                                          activeFile.filePath,
                                          resolved,
                                          resolvedContent
                                        )
                                      }
                                    />
                                  </div>
                                );
                              })()}
                          </>
                        ) : (
                          <div className="branch-merge__empty-state">
                            <div className="branch-merge__empty-state__content">
                              <h3>No region selected</h3>
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  );
                })()}
              </div>
            ) : selectedChanges && selectedChanges.length > 0 ? (
              <div className="branch-merge__conflict-details">
                <div className="branch-merge__conflict-header">
                  <div className="branch-merge__conflict-info">
                    <h3>{selectedKey}</h3>
                    <span className="branch-merge__conflict-count">
                      {selectedChanges.length} file change{selectedChanges.length !== 1 ? "s" : ""}
                    </span>
                  </div>
                </div>
                <div className="branch-merge__folder-diffs">
                  {selectedChanges.map((chg) => (
                    <div key={chg.filePath} className="branch-merge__file-diff">
                      <div className="branch-merge__file-header">
                        <h4>{chg.filePath}</h4>
                        <span>{chg.changeType}</span>
                      </div>
                      <div className="branch-merge__region-content branch-merge__region-content--patch">
                        <div className="branch-merge__region-section">
                          <div className="branch-merge__region-label">Patch</div>
                          <Editor
                            height="220px"
                            language="diff"
                            theme={CODE_EDITOR_THEME}
                            beforeMount={this.handleEditorBeforeMount}
                            value={chg.diffContent || "(no diff content)"}
                            options={{
                              readOnly: true,
                              minimap: { enabled: false },
                              scrollBeyondLastLine: false,
                              wordWrap: "on",
                              fontSize: 12
                            }}
                            loading={<div>Loading...</div>}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : showSetup ? (
              <MergeSetupPanel
                sourceBranchName={this.props.sd.currentBranch?.name}
                branches={branchOptions}
                inProgress={this.props.isLoading}
                onStart={(summary, description, targetBranch) =>
                  this.handleMergeRequest(summary, description, targetBranch)
                }
                onCancel={() => this.props.endMerge()}
              />
            ) : (
              <div className="branch-merge__empty-state">
                <div className="branch-merge__empty-state__content">
                  <div className="branch-merge__empty-state__icon-wrapper">
                    <KuikaSvg name="git-merge" width={24} height={24} />
                  </div>
                  <h3>{hasConflicts ? "Merge Conflicts Summary" : "Merge Changes Summary"}</h3>
                  <div className="branch-merge__summary">
                    <p>
                      <strong>{hasConflicts ? fileConflicts.length : mergeResult?.totalChanges || 0}</strong>{" "}
                      {hasConflicts ? "files have conflicts" : "changes detected"}
                    </p>
                    <p>
                      <strong>
                        {mergeResult?.conflictTree?.reduce((acc, item) => acc + (item.fileConflicts?.length ?? 0), 0)}
                      </strong>{" "}
                      {hasConflicts ? "total conflict regions" : "conflict regions"}
                    </p>
                    <p>
                      {hasConflicts
                        ? "Select a file to view conflicts or a folder to resolve them"
                        : "Select a file or folder to inspect merged change details"}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        {!showSetup && (
          <div className="branch-merge__actions-footer">
            <BranchMergeActions
              allConflictsResolved={allConflictsResolved}
              hasConflicts={hasConflicts}
              resolvedCount={this.getConflictResolutionStats(fileConflicts).resolved}
              totalCount={this.getConflictResolutionStats(fileConflicts).total}
              suggestionCount={this.getSuggestionCount(fileConflicts)}
              requiresApproval={mergeResult?.requiresApproval}
              completing={this.state.isCompletingMerge}
              onApplySuggestions={this.applyAllSuggestions}
              onJumpToNextUnresolved={this.jumpToNextUnresolvedRegion}
              onCompleteMerge={this.completeMerge(true)}
              onCancelMerge={this.completeMerge(false)}
            />
          </div>
        )}
        <BranchMergeModals
          showMergeToBranchModal={showMergeToBranchModal}
          showChangesFoundModal={showChangesFoundModal}
          branchOptions={branchOptions}
          currentBranchId={this.state.mergeData?.sourceBranchId}
          onCloseMergeToBranchModal={this.handleCloseMergeToBranchModal}
          onStartMerge={this.handleStartMerge}
          onMerge={this.handleMergeRequest}
          onCloseChangesFoundModal={this.handleCloseChangesFoundModal}
          onIgnoreChangesAndMerge={this.handleIgnoreChangesAndMerge}
          onCommitAndMerge={this.handleCommitAndMerge}
          changesFoundCommitMessage={this.state.changesFoundCommitMessage}
          changesFoundInProgress={this.state.changesFoundInProgress}
          onChangesFoundCommitMessageChange={this.handleChangesFoundCommitMessageChange}
        />
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch: any) => ({
  setNextCallbackFunction: (cb?: () => void) => dispatch(setNextCallbackFunction(cb))
});

const branchMerge = connect(mapDispatchToProps)(withSD<IBranchMergeProps>(BranchMergeComponent));
export { branchMerge as BranchMerge };
